import type { PartialMessages } from './es';

// Français: toute l’interface du tableau de bord. Chaque texte de l’espagnol (la source) existe ici ;
// un test (complete.test.ts) vérifie que rien ne manque et que les chiffres restent aux mêmes endroits.
export const fr: PartialMessages = {
  shell: {
    product: 'L’accessibilité, pas à pas',
    headline: 'Un meilleur site commence par savoir ce qu’il faut améliorer.',
    intro:
      'Analysez vos sites, repérez les obstacles et organisez le travail de votre équipe au même endroit.',
    connect: 'Connectez votre site',
    connectHint: 'Ajoutez votre domaine et choisissez son profil réglementaire.',
    understand: 'Comprenez les obstacles',
    understandHint: 'Consultez les problèmes, les priorités et comment les corriger.',
    improve: 'Avancez avec votre équipe',
    improveHint: 'Passez en revue les résultats et consignez vos décisions.',
    note: 'Les scores sont indicatifs. L’analyse automatique doit être complétée par une revue manuelle pour évaluer le respect des exigences d’accessibilité.',
  },
  app: {
    name: 'SiteNorma',
    home: 'SiteNorma, accueil du tableau de bord',
    website: 'SiteNorma, accueil du site',
    skip: 'Aller au contenu',
    language: 'Langue',
    userMenu: 'Menu utilisateur',
    signOut: 'Se déconnecter',
    loading: 'Chargement…',
    retry: 'Réessayer',
    newTab: 's’ouvre dans un nouvel onglet',
  },
  password: {
    show: 'Afficher le mot de passe',
    hide: 'Masquer le mot de passe',
    rules: {
      min: 'Au moins {{min}} caractères',
      max: 'Pas plus de {{max}} caractères',
      met: 'respecté',
      unmet: 'non respecté',
    },
    errors: {
      min: 'Le mot de passe doit comporter au moins {{min}} caractères.',
      max: 'Le mot de passe ne peut pas dépasser {{max}} caractères.',
    },
    nowVisible: 'Le mot de passe est affiché à l’écran.',
    nowHidden: 'Le mot de passe est masqué.',
  },
  notFound: {
    title: 'Page introuvable',
    text: 'Cette adresse n’existe pas ou a changé.',
    home: 'Aller au tableau de bord',
  },
  form: {
    requiredNote: 'Les champs marqués d’un * sont obligatoires.',
  },
  errors: {
    title: 'Un problème est survenu',
    generic: 'Nous n’avons pas pu terminer la demande. Veuillez réessayer.',
    required: 'Remplissez ce champ.',
    tooShort: 'Saisissez au moins {{min}} caractères.',
    invalidCredentials: 'L’adresse e-mail ou le mot de passe n’est pas correct.',
    userExists:
      'Il existe déjà un compte avec cette adresse e-mail. Connectez-vous ou réinitialisez votre mot de passe.',
  },
  forms: {
    email: {
      required: 'Saisissez votre adresse e-mail.',
      invalid: 'Saisissez une adresse e-mail valide.',
    },
    password: {
      required: 'Saisissez votre mot de passe.',
    },
    privacy: {
      required: 'Vous devez cocher la case relative à la confidentialité pour créer le compte.',
    },
    verifyLink: 'Ce lien n’est pas valide ou a expiré. Demandez un nouvel e-mail de vérification.',
    limit: {
      before: 'Trop de tentatives. Vous pourrez réessayer dans',
      seconds_one: '{{count}} seconde.',
      seconds_other: '{{count}} secondes.',
      over: 'Vous pouvez réessayer.',
      unknown: 'Trop de tentatives. Patientez un instant et réessayez.',
    },
  },
  google: {
    or: 'ou',
    login: 'Continuer avec Google',
    signup: "S'inscrire avec Google",
    connecting: 'Connexion à Google…',
    errors: {
      cancelled:
        'Vous avez annulé la connexion avec Google. Vous pouvez réessayer ou utiliser votre adresse e-mail et votre mot de passe.',
      noAccount: "Aucun compte SiteNorma n'existe pour ce compte Google. Créez-en un d'abord.",
      notLinked:
        "Un compte existe déjà avec l'adresse e-mail de votre compte Google, mais il n'est pas encore vérifié. Vérifiez-le avec le lien que nous vous avons envoyé ou connectez-vous avec votre mot de passe.",
      notVerified:
        "Google n'a pas confirmé cette adresse e-mail : nous ne pouvons donc pas l'utiliser pour votre compte.",
      failed:
        "Nous n'avons pas pu continuer avec Google. Réessayez ou utilisez votre adresse e-mail et votre mot de passe.",
    },
  },
  login: {
    title: 'Connexion',
    subtitle: 'Accédez à votre compte avec votre e-mail et votre mot de passe.',
    email: 'Adresse e-mail',
    password: 'Mot de passe',
    submit: 'Se connecter',
    forgot: 'Mot de passe oublié ?',
    noAccount: 'Vous n’avez pas de compte ?',
    signup: 'Créez votre compte',
    submitting: 'Connexion…',
  },
  signup: {
    title: 'Créez votre compte SiteNorma',
    benefit: 'Gérez vos analyses d’accessibilité et consultez leurs résultats au même endroit.',
    privacy: {
      label:
        'J’ai lu et j’accepte la <privacy>politique de confidentialité</privacy> et les <terms>conditions d’utilisation</terms>. Nous utiliserons votre adresse e-mail pour gérer votre compte et vous envoyer des communications liées au service.',
      language: 'Ces documents sont disponibles en anglais.',
    },
    email: 'Adresse e-mail',
    password: 'Mot de passe',
    verify: 'Pour activer votre compte, vous devrez vérifier votre adresse e-mail.',
    submit: 'Créer le compte',
    submitting: 'Création du compte…',
    haveAccount: 'Vous avez déjà un compte ?',
    login: 'Connectez-vous',
  },
  verify: {
    title: 'Vérifiez votre adresse e-mail',
    sent: 'Nous avons envoyé un e-mail de vérification à {{email}}. Ouvrez le lien pour activer votre compte. Le lien expire dans une heure.',
    notSent:
      'Votre compte est créé, mais nous n’avons pas pu envoyer l’e-mail de vérification à {{email}}. Il reste à vérifier : réessayez, inutile de vous inscrire à nouveau.',
    pendingLogin:
      'Pour vous connecter, vous devez vérifier votre adresse e-mail. Nous avons envoyé un lien à {{email}} ; si vous ne le retrouvez plus, vous pouvez en demander un autre.',
    spam: 'Si vous ne trouvez pas le message, consultez vos courriers indésirables et vérifiez que l’adresse est correcte.',
    resend: 'Renvoyer l’e-mail de vérification',
    resending: 'Envoi…',
    resent: 'Nous avons envoyé un autre e-mail de vérification à {{email}}.',
    wait: 'Vous pourrez demander un autre e-mail dans',
    over: 'Vous pouvez renvoyer l’e-mail de vérification.',
    correct: 'Corriger l’adresse e-mail',
    newEmail: 'Adresse e-mail correcte',
    correctHint:
      'Nous enverrons le lien à la nouvelle adresse. Les liens envoyés à l’ancienne cesseront de fonctionner et votre compte restera en attente jusqu’à la vérification de la nouvelle.',
    correctSubmit: 'Enregistrer et envoyer la vérification',
    correctSubmitting: 'Enregistrement…',
    cancel: 'Annuler',
    corrected:
      'Nous avons envoyé un e-mail de vérification à {{email}}. Les liens envoyés à l’ancienne adresse ne fonctionnent plus.',
    sendFailed:
      'Nous n’avons pas pu envoyer l’e-mail de vérification. Votre compte reste en attente : réessayez.',
    credentials: 'Nous n’avons pas pu vérifier le compte. Connectez-vous à nouveau pour continuer.',
    back: 'Retour à la connexion',
    done: {
      okTitle: 'Adresse e-mail vérifiée',
      ok: 'Votre adresse e-mail a été vérifiée. Vous pouvez maintenant vous connecter à SiteNorma.',
      alreadyTitle: 'Compte déjà vérifié',
      already: 'Votre compte est déjà vérifié.',
      invalidTitle: 'Lien non valide',
      invalid: 'Ce lien n’est pas valide ou a expiré. Demandez un nouvel e-mail de vérification.',
      noneTitle: 'Vérifiez votre adresse e-mail',
      none: 'Pour vérifier votre adresse e-mail, ouvrez le lien du message que nous vous avons envoyé. Si vous ne l’avez pas, vous pouvez en demander un nouveau.',
      login: 'Se connecter',
      request: 'Demander un nouvel e-mail',
    },
    request: {
      title: 'Demander un nouvel e-mail de vérification',
      intro: 'Saisissez l’adresse e-mail avec laquelle vous avez créé votre compte.',
      email: 'Adresse e-mail',
      submit: 'Demander un nouvel e-mail',
      submitting: 'Demande en cours…',
      sent: 'S’il existe un compte en attente de vérification associé à cette adresse e-mail, vous recevrez un nouveau lien. Consultez aussi vos courriers indésirables.',
    },
  },
  forgot: {
    title: 'Retrouvez l’accès à votre compte',
    sentTitle: 'Consultez vos e-mails',
    intro:
      'Saisissez l’adresse e-mail de votre compte pour demander un lien de réinitialisation de votre mot de passe.',
    email: 'Adresse e-mail',
    submit: 'Demander le lien',
    sent: 'Si un compte est associé à cette adresse e-mail, vous recevrez un lien pour réinitialiser votre mot de passe. Consultez aussi vos courriers indésirables.',
    back: 'Retour à la connexion',
    submitting: 'Demande du lien…',
  },
  reset: {
    title: 'Définissez un nouveau mot de passe',
    doneTitle: 'Mot de passe mis à jour',
    password: 'Nouveau mot de passe',
    submit: 'Enregistrer le mot de passe',
    done: 'Votre mot de passe a bien été mis à jour.',
    invalid:
      'Ce lien n’est pas valide, a expiré ou a déjà été utilisé. Demandez-en un nouveau pour réinitialiser votre mot de passe.',
    login: 'Se connecter',
    submitting: 'Enregistrement…',
    another: 'Demander un nouveau lien',
    pending:
      'Pour vous connecter, vous devez encore vérifier votre adresse e-mail. À la connexion, nous vous aiderons à le faire.',
  },
  organization: {
    title: 'Créez votre organisation',
    intro: 'Votre organisation regroupe vos sites et les personnes avec qui vous y travaillez.',
    name: 'Nom de l’organisation',
    nameHint: 'Par exemple, le nom de votre entreprise ou de votre agence.',
    submit: 'Créer l’organisation',
    nav: 'Organisation',
    navMembers: 'Personnes',
    navBilling: 'Offre et facturation',
  },
  billing: {
    title: 'Offre et facturation',
    loadFailed: 'Nous n’avons pas pu charger l’offre. Réessayez.',
    myPlan: 'Mon offre',
    planName: 'Offre {{plan}}',
    includes: 'Comprend',
    pagesLimit: 'Pages analysées par mois',
    pagesPerMonth_one: '{{count}} page',
    pagesPerMonth_other: '{{count}} pages',
    pagesUnlimited: 'Illimitées',
    wpLimit: 'Sites liés avec WordPress',
    wpSites_one: 'Jusqu’à {{count}} site',
    wpSites_other: 'Jusqu’à {{count}} sites',
    wpUnlimited: 'Illimités',
    selectPages: 'Sélection manuelle des pages',
    selectPagesYes: 'Incluse',
    selectPagesNo: 'Non incluse',
    seeOtherPlans: 'Voir les autres offres',
    usageOfMonth: 'Usage de {{month}}',
    pagesAnalysed_one: '{{count}} page du quota de ce mois',
    pagesAnalysed_other: '{{count}} pages du quota de ce mois',
    pagesUsedOf: '{{used}} pages du quota de ce mois sur {{limit}}',
    pagesAvailable_one: '{{count}} page disponible',
    pagesAvailable_other: '{{count}} pages disponibles',
    monthlyQuotaUnlimited: 'Quota mensuel : illimité.',
    usageLabel: 'Pages utilisées du quota mensuel',
    usageHint:
      'Additionne les pages de toutes vos analyses de ce mois : chacune compte. Dans « Vos sites », « Pages analysées » n’additionne que la dernière analyse de chaque site.',
    usageReserved_one: '{{count}} page réservée pour des analyses en file ou en cours.',
    usageReserved_other: '{{count}} pages réservées pour des analyses en file ou en cours.',
    quotaResets: 'Le compteur se réinitialise le {{date}}.',
    managementTitle: 'Gestion et facturation',
    managementFree: 'Consultez d’autres offres ou contactez notre équipe pour gérer votre abonnement.',
    managementPaid: 'Consultez d’autres offres ou contactez notre équipe pour gérer votre abonnement.',
    readOnly:
      'Vous pouvez consulter l’offre et l’usage. Pour la changer ou gérer l’abonnement, demandez à un propriétaire ou administrateur de l’organisation.',
    seePlans: 'Voir les offres',
    contactSupport: 'Contacter le support',
    assigned:
      'Votre offre est gérée par notre équipe et un achat ne la modifie pas. Si vous devez la changer, écrivez-nous.',
    purchase: {
      title: 'Choisissez votre offre',
      intro:
        'Vous payez les pages que nous analysons chaque mois, ordinateur et mobile inclus. Les prix s’entendent hors TVA.',
      period: 'Période de facturation',
      month: 'Mensuelle',
      year: 'Annuelle',
      yearHint: '2 mois offerts',
      perMonth: '{{price}} par mois',
      perYear: '{{price}} par an',
      equivalent: 'Équivaut à {{price}} par mois',
      buy: 'Choisir {{plan}}',
      redirecting: 'Ouverture du paiement sécurisé…',
      secure:
        'Le paiement se fait sur une page sécurisée de Stripe : SiteNorma ne voit ni ne conserve les données de votre carte. Vous pouvez résilier à tout moment et l’offre reste active jusqu’à la fin de la période payée.',
      custom: 'Besoin de plus de volume ? Parlez-nous',
      errors: {
        failed: 'Nous n’avons pas pu ouvrir le paiement. Réessayez.',
        'suscripcion-activa':
          'Vous avez déjà un abonnement. Pour changer d’offre, utilisez « Gérer l’abonnement ».',
        'plan-asignado': 'Votre offre est gérée par notre équipe et un achat ne la modifie pas.',
        limite: 'Vous avez fait trop de tentatives. Patientez un peu et réessayez.',
        permisos:
          'Seuls les propriétaires et les administrateurs de l’organisation peuvent souscrire une offre.',
      },
    },
    subscription: {
      title: 'Votre abonnement',
      line: 'Offre {{plan}}, facturation {{period}} : {{price}} (hors TVA).',
      month: 'mensuelle',
      year: 'annuelle',
      renews: 'Il se renouvelle le {{date}}.',
      cancels:
        'L’abonnement est résilié. Vous conservez l’offre {{plan}} jusqu’au {{date}} ; ensuite, vous revenez à l’offre Gratuite sans perdre vos données.',
      pastDue:
        'Nous n’avons pas pu encaisser le dernier paiement. Mettez à jour votre moyen de paiement pour ne pas perdre l’offre.',
      manage: 'Gérer l’abonnement',
      manageHint:
        'Changez d’offre, mettez à jour votre carte ou vos informations de facturation, ou résiliez l’abonnement. Une page sécurisée de Stripe s’ouvre.',
      portalFailed: 'Nous n’avons pas pu ouvrir la gestion de l’abonnement. Réessayez.',
    },
    return: {
      paid: 'Paiement reçu. Votre offre {{plan}} est désormais active.',
      confirming: 'Nous confirmons le paiement auprès de Stripe. Cela peut prendre quelques secondes.',
      cancelled: 'Aucun montant n’a été prélevé. Vous pouvez choisir une offre quand vous le souhaitez.',
    },
    summaries: {
      gratis: 'Pour une première revue de vos pages clés.',
      pro: 'Pour les organisations qui doivent se conformer et le documenter.',
      agencia: 'Pour les agences qui gèrent les sites de leurs clients.',
      'a-medida': 'Pour les grands volumes et les organisations aux besoins propres.',
      infinita: 'Accès complet à SiteNorma sans limites d’usage.',
    },
    features: {
      gratis: [
        'Analyse d’une page et analyse rapide',
        'Accessibilité automatique selon WCAG 2.2 A et AA, sur ordinateur et mobile',
      ],
      pro: [
        'Sites et utilisateurs illimités',
        'Analyses complètes sur les sites vérifiés',
        'Conformité réglementaire et revue manuelle guidée',
        'Rapports PDF',
      ],
      agencia: ['Tout ce qui est dans Pro', 'Plus de capacité pour les équipes qui gèrent plusieurs sites'],
      'a-medida': ['Tout ce qui est dans Agence', 'Conditions adaptées à votre organisation'],
      infinita: [],
    },
  },
  home: {
    figures: {
      sites: 'Sites',
      verified_one: '{{count}} vérifié',
      verified_other: '{{count}} vérifiés',
      problems: 'Types de problèmes détectés',
      problemsHelp: 'Que sont les types de problèmes',
      problemsHelpText:
        'Un type de problème est un défaut (par exemple, des images sans texte alternatif). Les éléments concernés sont les endroits précis où il apparaît. Le total additionne la dernière analyse de chaque site, sans ce qui a été écarté.',
      elements_one: '{{count}} élément concerné',
      elements_other: '{{count}} éléments concernés',
      elementsCapped_one: '{{count}}+ élément concerné',
      elementsCapped_other: '{{count}}+ éléments concernés',
      problemsHelpCapped:
        'Le « + » indique que l’analyse a cessé de compter des éléments dans une vérification : le total réel peut être plus élevé.',
      global_one: '{{count}} global',
      global_other: '{{count}} globaux',
      none: 'Pas encore d’analyse',
      pages: 'Pages analysées',
      pagesHint: 'Seulement la dernière analyse de chaque site, pas l’usage du mois',
      pagesHintSome: 'Seulement la dernière de {{analysed}} sites sur {{total}}, pas l’usage du mois',
    },
    card: {
      pending: 'En attente d’analyse',
      never: 'Préparez la première analyse pour voir les résultats.',
      requested: 'Demandée le {{date}}',
      attempt: 'Dernière tentative : {{date}}',
      lastAnalysis: 'Dernière analyse : {{date}}',
      lastCompleted: 'Dernière analyse terminée : {{date}}',
      estimate: 'Estimation :',
      noEstimate: 'Pas encore d’estimation',
      estimateHelp: 'Que signifie l’estimation automatique',
      estimateHelpText:
        'Il s’agit d’une estimation automatique sur 100. Ce n’est ni un pourcentage de conformité ni la conformité légale : les tests automatiques ne détectent qu’une partie des obstacles.',
      estimateMethod: 'Comment elle est calculée',
      deltaUp_one: 'A progressé de {{count}} point par rapport à l’analyse précédente.',
      deltaUp_other: 'A progressé de {{count}} points par rapport à l’analyse précédente.',
      deltaDown_one: 'A régressé de {{count}} point par rapport à l’analyse précédente.',
      deltaDown_other: 'A régressé de {{count}} points par rapport à l’analyse précédente.',
      problems_one: '{{count}} type de problème',
      problems_other: '{{count}} types de problèmes',
      priority: {
        alta_one: '{{count}} de priorité haute',
        alta_other: '{{count}} de priorité haute',
        media_one: '{{count}} de priorité moyenne',
        media_other: '{{count}} de priorité moyenne',
        baja_one: '{{count}} de priorité basse',
        baja_other: '{{count}} de priorité basse',
      },
      notAnalysed_one: '{{count}} page n’a pas pu être analysée.',
      notAnalysed_other: '{{count}} pages n’ont pas pu être analysées.',
      ownership: {
        verified: 'Propriété vérifiée',
        unverified: 'Propriété non vérifiée',
        checking: 'Vérification de la propriété en cours',
        failed: 'La propriété n’a pas pu être vérifiée',
      },
      actions: {
        prepare: 'Analyser mon site',
        results: 'Voir les résultats',
        progress: 'Voir l’analyse',
        retry: 'Relancer l’analyse',
        open: 'Ouvrir le site',
      },
    },
    subtitle: 'Un lieu unique pour examiner et améliorer l’accessibilité de vos sites.',
    listTitle: 'Tous les sites',
    search: 'Rechercher par nom ou domaine',
    results: 'Sites trouvés : {{count}}',
    noResults: 'Aucun site ne correspond. Essayez un autre nom ou domaine.',
    title: 'Vos sites',
    organization: 'Organisation : {{name}} · Votre rôle : {{role}}',
    empty: 'Vous n’avez encore ajouté aucun site.',
    emptyHint:
      'Ajoutez votre premier site et commencez par une analyse rapide. Vérifiez sa propriété pour accéder aux analyses complètes.',
    emptyReadOnly: 'Quand une personne de votre organisation ajoutera un site, vous le verrez ici.',
    add: 'Ajouter un site',
    roles: {
      owner: 'Propriétaire',
      admin: 'Administrateur',
      editor: 'Éditeur',
      viewer: 'Lecteur',
    },
  },
  sites: {
    status: {
      verified: 'Propriété vérifiée',
      unverified: 'Propriété non vérifiée',
      checking: 'Vérification de la propriété en cours',
      failed: 'La propriété n’a pas pu être vérifiée',
    },
    profiles: {
      eaa: 'Service privé (Acte européen sur l’accessibilité)',
      'sector-publico': 'Secteur public',
      voluntario: 'Volontaire',
      'sin-definir': 'Non défini',
    },
    profileHints: {
      eaa: 'Pour les entreprises qui proposent aux consommateurs des services couverts par la loi (commerce en ligne, banque, transports…). Son application dépend de votre activité et de votre taille. Référence : loi espagnole 11/2023.',
      'sector-publico':
        'Pour les administrations et organismes du secteur public. Référence : décret royal 1112/2018.',
      voluntario:
        'Pour les sites sans obligation légale spécifique : WCAG 2.2 AA sert de référence technique.',
    },
    standards: {
      'EN301549-4.1.1': 'EN 301 549 v4.1.1 · WCAG 2.2 AA',
      'EN301549-3.2.1': 'EN 301 549 v3.2.1 · WCAG 2.1 AA',
      'WCAG22-AA': 'WCAG 2.2 AA',
      'WCAG21-AA': 'WCAG 2.1 AA',
    },
  },
  newSite: {
    title: 'Ajoutez votre site',
    intro: 'Saisissez l’adresse de votre site. Vous pourrez ensuite lancer l’analyse d’accessibilité.',
    url: 'Adresse du site',
    urlHint:
      'Saisissez l’adresse de la page que vous voulez analyser en premier. Par exemple, https://www.votresite.com',
    name: 'Nom du site (facultatif)',
    nameHint: 'Si vous le laissez vide, nous utiliserons le domaine du site.',
    normalized: 'Adresse qui sera enregistrée : {{url}}',
    existing: 'Vous avez déjà un site avec cette adresse : « {{name}} ».',
    open: 'Ouvrir le site',
    submit: 'Ajouter le site',
    submitting: 'Ajout du site…',
    cancel: 'Retour à vos sites',
    errors: {
      url: 'Saisissez une adresse web valide, par exemple, https://www.votresite.com',
      duplicada: 'Ce site existe déjà dans votre organisation.',
      permiso: 'Votre rôle ne permet pas d’ajouter des sites.',
    },
  },
  account: {
    menuProfile: 'Mon profil',
    menuSecurity: 'Sécurité',
    planLabel: 'Offre {{plan}}',
    nav: 'Votre compte',
    scope:
      'Ces paramètres sont personnels : ils ne dépendent d’aucun site ni de votre organisation. Les paramètres de chaque site se trouvent dans le site lui-même, sous « Paramètres ». L’offre et la facturation sont dans Organisation → Offre et facturation.',
    profile: {
      title: 'Mon profil',
      name: 'Nom',
      email: 'Adresse e-mail',
      emailHint:
        'C’est l’adresse avec laquelle vous vous connectez à SiteNorma. Elle ne peut pas être modifiée ici.',
      save: 'Enregistrer les modifications',
      saving: 'Enregistrement…',
      saved: 'Modifications enregistrées.',
    },
    security: {
      title: 'Sécurité',
      intro:
        'Modifiez le mot de passe avec lequel vous vous connectez. Vos autres appareils seront alors déconnectés ; cette session continue.',
      current: 'Mot de passe actuel',
      new: 'Nouveau mot de passe',
      submit: 'Changer le mot de passe',
      submitting: 'Modification…',
      done: 'Votre mot de passe a été modifié. Vos autres sessions ont été fermées.',
      currentWrong: 'Le mot de passe actuel n’est pas correct.',
    },
  },
  seal: {
    tab: 'Sceau d’accessibilité',
    title: 'Sceau d’accessibilité',
    intro:
      'Montrez sur votre site que vous avez réussi les vérifications d’accessibilité de SiteNorma. Le sceau permet de consulter les résultats et la date de la dernière revue, mais ce n’est pas une certification WCAG.', // claims-ok
    status: {
      inactivo: 'Non activé',
      vigente: 'Actif',
      caducado: 'Expiré',
      suspendido: 'Suspendu',
    },
    statusHint: {
      inactivo: 'Le sceau n’est affiché sur aucun site.',
      vigente: 'Vous remplissez les exigences et la dernière revue est récente.',
      caducado:
        'La dernière revue complète date de plus de {{days}} jours : lancez une nouvelle analyse complète pour le renouveler. En attendant, le sceau est affiché comme expiré.',
      suspendido:
        'Une exigence n’est plus remplie : le sceau est affiché comme suspendu et la fiche publique l’indique.',
    },
    requirements: {
      title: 'Vérifier les exigences',
      progress: '{{done}} exigences sur {{total}} remplies',
      recommended: 'Recommandé (ce n’est pas une exigence)',
      met: 'remplie',
      pending: 'en attente',
      lastAnalysis: 'Dernière analyse complète : {{date}} ({{pages}} pages analysées).',
    },
    criteria: {
      ownership: 'La propriété du site est vérifiée.',
      analysis: 'Il y a une analyse complète en vigueur, des {{days}} derniers jours.',
      noHigh: 'Il n’y a aucun problème de priorité haute en attente.',
      manual:
        'Toutes les vérifications manuelles applicables ont été revues, sans problème de priorité haute.',
    },
    recommended: {
      statement:
        'Le site a une déclaration d’accessibilité publiée. C’est un document distinct du sceau, mais nous le recommandons.',
    },
    fix: {
      ownership: 'Vérifier le site',
      analysis: 'Lancer une analyse complète',
      noHigh: 'Voir les problèmes',
      manual: 'Faire les revues manuelles',
      statement: 'Créer la déclaration',
    },
    scope: {
      title: 'Ce qui est vérifié',
      hint: 'Pages et vérifications à réussir',
      analysis:
        'Une analyse complète du site (jusqu’à 50 pages, sur ordinateur et mobile), terminée avec toutes les pages analysées, des {{days}} derniers jours.',
      checks:
        'Cette analyse ne laisse aucun problème de priorité haute en attente ; ce que l’équipe a écarté avec justification ne compte pas.',
      manual:
        'Toutes les vérifications manuelles applicables au site, avec une revue enregistrée et sans constat de priorité haute.',
      not: 'Les réussir ne revient pas à prouver le respect complet des WCAG : le sceau indique exactement cela et rien de plus.',
    },
    activate: {
      title: 'Activer le sceau',
      intro:
        'L’activer crée la fiche de vérification publique et le sceau est prêt à être mis sur votre site. Il n’apparaît pas sur votre site tant que vous ne l’ajoutez pas (étape suivante).',
      button: 'Activer le sceau',
      disabled_one: 'Il manque {{count}} exigence',
      disabled_other: 'Il manque {{count}} exigences',
      done: 'Sceau activé. Mettez-le maintenant dans le pied de page de votre site.',
      active: 'Le sceau est activé.',
    },
    withdraw: 'Retirer le sceau',
    withdrawTitle: 'Retirer le sceau ?',
    withdrawMessage:
      'Le sceau ne sera plus affiché sur votre site et la fiche publique ne fonctionnera plus.',
    withdrawn: 'Sceau retiré.',
    notEligible: 'Le site ne remplit plus toutes les exigences.',
    readOnly:
      'Votre rôle ne permet que de consulter le sceau. Demandez à une personne ayant le rôle d’éditeur ou supérieur de l’activer.',
    preview: {
      title: 'Aperçu',
      notReady_one:
        'Il ne peut pas encore être activé : il manque {{count}} exigence. Voici son aspect une fois remplie.',
      notReady_other:
        'Il ne peut pas encore être activé : il manque {{count}} exigences. Voici son aspect une fois remplies.',
      language: 'Langue',
      detected: '(détectée sur votre site)',
      theme: 'Fond du pied de page',
      themes: {
        light: 'Clair',
        dark: 'Foncé',
      },
      size: 'Taille',
      sizes: {
        s: 'Petit',
        m: 'Moyen',
        l: 'Grand',
      },
      states: {
        vigente: 'Actif',
        caducado: 'Expiré',
        suspendido: 'Suspendu',
      },
      footer: 'Voici son aspect dans un pied de page',
      footerLinks: 'Mentions légales · Confidentialité · Cookies',
      alt: 'Sceau d’accessibilité SiteNorma (aperçu)',
    },
    embed: {
      title: 'L’ajouter au pied de page de votre site',
      locked: 'Disponible une fois le sceau activé.',
      wordpress: {
        title: 'WordPress',
        how: 'Dans votre WordPress, ouvrez SiteNorma → Connexion et activez « Afficher le sceau sur mon site ». Choisissez la taille, la langue et la version claire ou foncée : le plugin le place dans le pied de page et ne l’affiche que tant que le sceau est actif.',
        open: 'Ouvrir le plugin dans votre WordPress',
        none: 'Avec le plugin SiteNorma, le sceau se place tout seul dans le pied de page de WordPress, sans toucher au code.',
        connect: 'Connecter le plugin',
      },
      html: {
        title: 'Copier le code HTML',
        hint: 'Pour Shopify, les sites sur mesure et d’autres gestionnaires : le code affiche le sceau comme une image SVG servie par SiteNorma, qui renvoie à la fiche de vérification publique.',
        steps: {
          one: 'Choisissez ci-dessus la langue, le fond et la taille.',
          two: 'Copiez le code.',
          three:
            'Collez-le dans le HTML du pied de page de votre site ou de votre thème. Il n’a besoin d’aucun script et ne modifie pas les styles de votre site.',
        },
        label: 'Code HTML',
        snippetHint:
          'Pour {{language}}, fond {{theme}} et taille {{size}}. L’image se met à jour toute seule si le sceau expire ou est suspendu.',
      },
    },
    verify: {
      title: 'Vérifier et maintenir la validité',
      intro:
        'Qui clique sur le sceau arrive sur une fiche publique de SiteNorma avec le nom et le domaine du site, son état, les dates de la dernière revue et d’expiration et le périmètre de ce qui a été vérifié.',
      open: 'Ouvrir la fiche publique',
      locked: 'La fiche est créée à l’activation du sceau.',
      renews: 'Il se renouvelle tout seul quand une nouvelle analyse complète remplit les exigences.',
      expires: 'Il expire {{days}} jours après la dernière analyse complète s’il n’y en a pas de nouvelle.',
      suspends: 'Il est suspendu si une analyse détecte que les exigences ne sont plus remplies.',
      live: 'La fiche et l’image du sceau reflètent le changement aussitôt : un site au sceau expiré ou suspendu ne l’affiche pas comme valide.',
      lastReview: 'Dernière revue : {{date}}.',
      validUntil: 'En vigueur jusqu’au {{date}} sans analyse complète plus récente.',
    },
  },
  statement: {
    tab: 'Déclaration d’accessibilité',
    title: 'Déclaration d’accessibilité',
    intro:
      'Créez la déclaration d’accessibilité de ce site en quatre étapes : choisissez les langues, renseignez les données une seule fois, relisez le document et approuvez-le. Avec plusieurs langues, un seul formulaire les génère toutes ; sur votre site, le script affiche celle de la page. C’est un document informatif sur l’état d’accessibilité, même s’il a des problèmes en attente. L’approuver ne le publie pas sur le site.',
    language: 'Langues de la déclaration',
    languages: {
      es: 'Español',
      ca: 'Català',
      en: 'English',
      fr: 'Français',
      de: 'Deutsch',
      prompt: 'Choisissez une ou plusieurs langues. Celles détectées sur votre site sont marquées.',
      noneDetected:
        'Nous n’avons pas encore détecté les langues de votre site. Choisissez celles dans lesquelles vous voulez la déclaration.',
      helpLabel: 'Aide sur les langues de la déclaration',
      help: 'Cochez toutes les langues dans lesquelles vous voulez la déclaration. Les données de l’étape suivante se renseignent une seule fois et génèrent le texte dans chaque langue. Sur votre site, un seul script affiche celle de la page.',
      detected: 'détectée',
      needOne: 'Choisissez au moins une langue pour continuer.',
      selectedOne:
        'La déclaration sera préparée en {{name}}. Cochez une autre langue si vous voulez retirer celle-ci ; il doit en rester au moins une.',
      selectedMany:
        'La déclaration sera préparée en {{count}} langues ({{list}}). Les données se renseignent une seule fois pour toutes.',
      removeApproved:
        'En continuant, la déclaration approuvée des langues retirées sera supprimée (y compris son historique).',
      next: 'Suivant',
      unsupported:
        'Il y a aussi des pages dans ces langues, pour lesquelles nous n’avons pas encore de modèle : {{list}}.',
      updated: 'Langues mises à jour.',
    },
    published: 'version {{n}} validée',
    draft: 'brouillon',
    none: 'non créée',
    cancel: 'Annuler',
    readOnly:
      'Votre rôle ne permet que de consulter la déclaration. Demandez à une personne ayant le rôle d’éditeur ou supérieur de la modifier et de la valider.',
    errors: {
      incomplete: 'La déclaration n’est pas encore complète : vérifiez ce qui manque à l’étape 4.',
      permission: 'Votre rôle ne permet pas de modifier la déclaration. Rien n’a été enregistré.',
      generic: 'Nous n’avons pas pu effectuer l’action. Réessayez ; rien n’a été publié.',
      noWordpress: 'Ce site n’a pas le plugin SiteNorma connecté dans WordPress.',
    },
    steps: {
      language: 'Langues',
      data: 'Renseigner les données',
      review: 'Générer et relire',
      validate: 'Relire et approuver',
    },
    status: {
      pendiente: 'En attente de validation',
      validada: 'Validée, pas encore publiée sur votre site',
      publicada: 'Publiée sur votre site',
      last: 'version {{n}}, approuvée le {{date}}',
      changes: 'il y a des modifications non approuvées',
    },
    data: {
      title: 'Renseigner les données',
      intro:
        'Nous ne demandons que l’essentiel : les coordonnées du responsable du site. Le reste provient de votre site, du profil réglementaire et des analyses. Ces données sont les mêmes pour toutes les langues choisies : inutile de les répéter.',
      entity: 'Entité responsable',
      name: 'Nom de l’entité',
      address: 'Adresse',
      email: 'Adresse e-mail',
      emailHint:
        'C’est aussi l’adresse de contact pour l’accessibilité, sauf si vous en indiquez une autre dans les options avancées.',
      website: 'Site',
      prefilled: 'Renseignés à partir de votre site. Vous pouvez les modifier si nécessaire.',
      siteName: 'Nom du site',
      siteUrl: 'Adresse du site',
      guidedTitle: 'Procédure de réclamation',
      advanced: 'Options avancées',
      advancedHint: 'Identifiant fiscal, téléphone, contact, périmètre, exceptions et mode d’évaluation',
      taxId: 'Identifiant fiscal (facultatif)',
      phone: 'Téléphone (facultatif)',
      contactEmail: 'E-mail de contact pour l’accessibilité (facultatif)',
      contactEmailHint: 'Si vous le laissez vide, l’e-mail de l’entité est utilisé.',
      contactUrl: 'Formulaire ou page de contact (facultatif)',
      responseDays: 'Délai de réponse en jours (facultatif)',
      scope: 'Périmètre (facultatif)',
      scopeHint:
        'Si la déclaration ne couvre pas tout le site (une section, un sous-domaine…), indiquez-le ici.',
      exceptions: 'Exceptions (facultatif)',
      exceptionsHint:
        'Contenu hors périmètre ou charge disproportionnée, avec vos propres mots et sa justification.',
      enforcement: 'Procédure de réclamation',
      enforcementHint:
        'Facultatif. Indiquez à qui et comment adresser une réclamation, si votre entité a une procédure. SiteNorma ne l’invente pas.',
      enforcementRequired:
        'Obligatoire dans le secteur public. Indiquez l’unité responsable et la procédure de réclamation applicable : SiteNorma ne l’invente pas.',
      preparationNote: 'Comment l’évaluation a été faite, avec vos mots (facultatif)',
      generate: 'Générer la déclaration',
      regenerate: 'Générer à nouveau la déclaration',
      generated: 'Déclaration générée dans toutes les langues choisies. Relisez-la.',
    },
    evaluation: {
      label: 'Évaluation',
      state: {
        pendiente: 'En attente',
        incompleta: 'Incomplète',
        completada: 'Terminée',
      },
      pendingText: 'Lancez l’analyse et la revue manuelle pour préparer la déclaration.',
      incompleteBarriers:
        'Des obstacles ont été détectés. Terminez la revue manuelle pour fixer la conformité.',
      incompleteClear:
        'L’analyse automatique n’a détecté aucun obstacle. Terminez la revue manuelle pour fixer la conformité.',
      pendingChecks: 'Voir les vérifications en attente',
      completePartial:
        'La revue justifie une conformité partielle. Les manquements sont dans la déclaration.',
      completeUndeclared: 'La revue ne permet pas de déclarer un degré de conformité.',
    },
    document: {
      label: 'Document',
      draft: 'Brouillon',
      approved: 'Approuvé',
    },
    evidence: {
      auto: 'Analyse automatique',
      autoValue:
        'Dernière analyse le {{date}} : {{pages}} pages analysées et {{problems}} problèmes en attente.',
      noScan:
        'Aucune analyse n’est encore terminée. Lancez-en une dans « Analyses » pour que la déclaration la prenne en compte.',
      manual: 'Revue manuelle',
      manualValue:
        '{{registered}} critères sur {{total}} revus. Les autres n’ont pas été vérifiés et la déclaration le dit.',
      outdated: 'Il y a une analyse plus récente que celle utilisée pour ce texte.',
    },
    review: {
      title: 'Générer et relire',
      intro:
        'SiteNorma a généré la déclaration à partir de vos données, du profil réglementaire du site et de la référence technique ({{standard}}). Voici à quoi elle ressemblera sur votre site.',
      generated: 'Déclaration mise à jour avec la dernière analyse.',
      update: 'Mettre à jour avec la dernière analyse',
      previewLabel: 'Aperçu de la déclaration',
      empty: 'Il n’y a pas encore de déclaration : renseignez les données pour la générer.',
      readOnly:
        'Le texte est en lecture seule. Si vous devez changer quelque chose, retournez aux données : la déclaration est générée à nouveau.',
      edit: 'Modifier les données',
      continue: 'Relire et approuver',
    },
    validate: {
      title: 'Relire et approuver',
      blocked: 'Il manque des informations obligatoires pour approuver.',
      fixData: 'Renseigner les données',
      regime: 'Le secteur public exige une situation de conformité concrète.',
      openAnalysis: 'Aller à l’analyse',
      seeText: 'Voir la déclaration',
      confirm: 'J’ai relu le document et je confirme que les informations sont exactes.',
      confirmPending:
        'J’ai relu le document dans les langues en attente et je confirme que les informations sont exactes.',
      action: 'Approuver le document',
      actionPending: 'Approuver les langues en attente',
      clarification: 'Approuver n’atteste pas l’accessibilité du site.',
      back: 'Retour à la relecture',
      done: 'Version {{n}} approuvée dans toutes les langues prêtes. Choisissez maintenant comment la publier.',
      donePending: 'Approuvé : {{list}}. Vous pouvez maintenant copier le HTML de chaque langue.',
      pendingLangs_one:
        'Il reste à approuver {{list}}. Sans cela, le script et le HTML n’incluent que les langues déjà approuvées.',
      pendingLangs_other:
        'Il reste à approuver {{list}}. Sans cela, le script et le HTML n’incluent que les langues déjà approuvées.',
      unvalidatedChanges:
        'Il y a des modifications après la version {{n}} : votre site garde cette version jusqu’à l’approbation de la nouvelle.',
    },
    problems: {
      entity: 'Le nom de l’entité manque.',
      address: 'L’adresse de l’entité manque.',
      email: 'L’e-mail de l’entité manque.',
      site: 'L’adresse du site web manque.',
      contact: 'Une adresse e-mail de contact pour l’accessibilité manque.',
      enforcement: 'La procédure de réclamation manque (obligatoire dans le secteur public).',
      conformity:
        'Le secteur public exige une situation de conformité concrète et l’évaluation ne la justifie pas.',
      blank: 'Le texte contient encore des données à compléter : retournez aux données.',
      empty: 'Il n’y a pas encore de déclaration : renseignez les données pour la générer.',
    },
    publication: {
      title: 'Publier sur votre site',
      intro:
        'La version {{n}} est approuvée ({{date}}). L’approuver ne la met pas sur votre site : choisissez une méthode, suivez les étapes et marquez-la quand elle est sur la page. Le script et le lien affichent toujours la dernière version approuvée.',
      choose: 'Choisissez comment ajouter la déclaration',
      languageScript:
        'Un seul script couvre {{list}}. Il affiche la langue de la page (attribut lang ou chemin) dès le chargement, sans seconde requête.',
      languageLink: 'Ce lien est celui de la déclaration en {{name}}. Chaque langue a le sien.',
      languageHtml:
        'Copiez le HTML de chaque langue et collez-le sur la page correspondante. Choisissez la langue et appuyez sur copier.',
      mark: {
        script: 'J’ai ajouté le script',
        link: 'J’ai lié la page',
        html: 'J’ai collé le HTML',
        wordpress: 'J’ai demandé de le publier dans WordPress',
      },
      markedOn: 'Marquée comme publiée le {{date}}.',
      saved: {
        script: 'Marquée comme publiée avec le script.',
        link: 'Marquée comme publiée avec le lien.',
        html: 'Marquée comme publiée avec le HTML.',
        wordpress: 'Demande de publication WordPress enregistrée.',
      },
    },
    delivery: {
      recommended: 'Recommandé',
      script: 'Extrait de code',
      scriptSummary: 'Se met à jour seule à l’approbation d’une nouvelle version et suit la langue de la page.',
      scriptHint: 'Copiez le code. Un message de chargement s’affiche jusqu’à ce que le texte soit prêt.',
      link: 'Lien hébergé',
      linkSummary: 'Page hébergée par SiteNorma qui se met à jour seule à l’approbation.',
      linkHint: 'Copiez ce lien et utilisez-le sur votre site, par exemple dans le pied de page.',
      html: 'HTML',
      htmlSummary: 'Copiez le HTML à la main. Vous devrez le coller à nouveau si vous approuvez une nouvelle version.',
      htmlHint: 'Copiez ce HTML et insérez-le dans la page de votre site.',
      htmlLangs: 'Langue du HTML',
      htmlNote:
        'Ce HTML ne se met pas à jour seul : si vous approuvez une autre version, recopiez-le. Pour une mise à jour automatique, utilisez l’extrait de code ou le lien.',
      wordpress: 'WordPress',
      wordpressSummary: 'Le plugin SiteNorma crée ou met à jour la page dans votre WordPress.',
      wordpressHint:
        'Marquez cette option et publiez depuis le plugin SiteNorma dans WordPress (ou attendez la synchronisation horaire).',
      wordpressNote: 'Le plugin confirme quand la page est publiée sur votre site.',
      wordpressConnect: 'Connectez le plugin SiteNorma dans WordPress pour y publier la déclaration.',
      wordpressGo: 'Aller à Intégrations → WordPress',
      stepCopy: 'Étape 1 : copiez ce code',
      stepPaste: 'Étape 2 : collez-le dans la page de votre site où vous voulez que la déclaration apparaisse.',
      stepCopyLink: 'Étape 1 : copiez ce lien',
      stepPasteLink: 'Étape 2 : liez-le depuis votre site, par exemple dans le pied de page.',
      stepCopyHtml: 'Étape 1 : copiez le HTML en {{name}}',
      quality:
        'Pendant l’arrivée du script, « Chargement… » s’affiche dans la langue de la page. Le texte de toutes les langues approuvées est dans le script lui-même, se charge de façon asynchrone et n’ajoute aucune dépendance ; il utilise de vrais titres et listes et hérite de la typographie et des couleurs de votre site. S’il ne pouvait pas s’afficher, un lien vers la déclaration reste.',
      copy: 'Copier',
      copied: 'Copié',
    },
    versions: {
      title: 'Versions',
      hint: 'Chaque version validée est conservée avec sa date et la personne qui l’a validée. Vous pouvez en récupérer une comme brouillon pour la mettre à jour.',
      version: 'Version {{n}}',
      by: 'validée par {{name}}',
      view: 'Voir',
      hide: 'Masquer',
      restore: 'Récupérer comme brouillon',
      restored:
        'La version {{n}} est maintenant le brouillon. Rien n’est publié tant que vous ne l’avez pas validée.',
      modify: 'Modifier les données et créer une nouvelle version',
    },
  },
  pagesList: {
    title: 'Pages analysées',
    intro:
      'Inventaire du site (pas seulement la dernière analyse) : chaque page montre son dernier résultat.',
    figures: {
      analysed: 'Pages analysées',
      pending: 'Avec des problèmes en attente',
      clean: 'Sans problèmes en attente',
    },
    empty: 'Aucune page n’a encore été analysée. Commencez par une analyse depuis « Analyses ».',
    notFound: 'Cette page ne figure plus dans les résultats de ce site.',
    noMatch: 'Aucune page ne correspond à votre choix. Modifiez le filtre ou la recherche.',
    filter: {
      label: 'Afficher',
      todas: 'Toutes les pages',
      pendientes: 'Avec des problèmes en attente',
      limpias: 'Sans problèmes en attente',
    },
    pending_one: '{{count}} problème en attente',
    pending_other: '{{count}} problèmes en attente',
    none: 'Aucun problème en attente',
    verified_one: '{{count}} correction vérifiée',
    verified_other: '{{count}} corrections vérifiées',
    lastAnalysis: 'Dernière analyse : {{date}}',
  },
  nav: {
    label: 'Tableau de bord',
    sites: 'Sites',
    organization: 'Organisation',
    users: 'Utilisateurs',
    menu: 'Menu',
    integrations: 'Intégrations',
  },
  mcp: {
    title: 'MCP pour Cursor et d’autres agents',
    intro:
      'L’agent lit vos analyses et applique les corrections dans le code local de votre site. Il n’écrit pas sur le site publié : vous publiez les changements.',
    note: 'Le jeton est personnel et agit avec vos permissions. Ne le partagez pas et ne le mettez pas dans le dépôt. S’il est perdu, révoquez-le et créez-en un nouveau.',
    create: {
      title: 'Créer un jeton',
      name: 'Nom (facultatif)',
      namePlaceholder: 'ex. Cursor du portable',
      button: 'Créer le jeton',
      working: 'Création…',
      limit: 'Vous avez atteint le maximum de {{max}} jetons actifs. Révoquez-en un pour en créer un autre.',
    },
    created: {
      title: 'Lien pour l’agent',
      body: 'Copiez-le et collez-le dans le chat de l’agent (Cursor ou un autre). L’agent lit les instructions du lien et configure le MCP de SiteNorma. Le lien contient le jeton, ne s’affiche qu’une fois et ne doit ni être partagé ni collé ailleurs.',
      copyLink: 'Copier le lien',
      linkCopied: 'Lien copié.',
      copy: 'Copier le jeton',
      copied: 'Jeton copié.',
      configTitle: 'Configuration pour d’autres agents',
      configHint:
        'Le lien fait écrire cette configuration par l’agent. La commande est le chemin du lanceur sur cet ordinateur. Si ce fichier n’existe pas, l’agent le dira.',
      copyConfig: 'Copier la configuration',
      configCopied: 'Configuration copiée.',
      close: 'J’ai enregistré le jeton',
    },
    steps: {
      title: 'Comment l’utiliser',
      one: 'Créez un jeton sur cette page.',
      two: 'Copiez le lien et collez-le dans le chat de l’agent. Cela configure le MCP de SiteNorma.',
      three:
        'Ouvrez le code de votre site (thème ou dépôt) dans l’éditeur et demandez à l’agent de corriger les problèmes SiteNorma.',
      four: 'Publiez les changements et, dans le tableau de bord, lancez « Vérifier la correction » ou une nouvelle analyse.',
    },
    list: {
      title: 'Jetons actifs',
      empty: 'Il n’y a encore aucun jeton MCP.',
      tableLabel: 'Jetons MCP (le tableau défile)',
      name: 'Nom',
      prefix: 'Jeton',
      owner: 'Créé par',
      created: 'Créé',
      lastSeen: 'Dernière utilisation',
      never: 'Aucune encore',
      actions: 'Actions',
      revoke: 'Révoquer',
      revokeTitle: 'Révoquer « {{name}} » ?',
      revokeBody:
        'L’agent qui utilise ce jeton ne pourra plus lire vos analyses. Vous pourrez en créer un autre quand vous voudrez.',
      revokeConfirm: 'Révoquer',
      cancel: 'Annuler',
      revoked: 'Le jeton « {{name}} » a été révoqué.',
      usage: '{{mine}} de vos {{max}} jetons actifs dans cette organisation.',
    },
    errors: {
      limit: 'Vous avez atteint le maximum de jetons actifs.',
      generic: 'L’action n’a pas pu être terminée. Réessayez.',
    },
  },
  integrations: {
    title: 'Intégrations',
    intro:
      'Connectez SiteNorma à d’autres plateformes et à votre environnement de développement pour consulter les résultats et corriger les problèmes d’accessibilité.',
    nav: 'Sections des intégrations',
    mcp: 'MCP',
    wordpress: 'WordPress',
  },
  wordpress: {
    title: 'Extension WordPress',
    intro:
      'Installez l’extension SiteNorma sur votre WordPress et vous verrez les résultats de ce site sans quitter son tableau de bord. L’extension n’affiche que les données du site où elle est installée : les analyses complètes et les rapports PDF se font toujours ici, sur la plateforme.',
    note: 'Les résultats sont une estimation automatique : les tests automatiques ne détectent que des problèmes et le reste nécessite une revue manuelle. L’extension affiche les mêmes informations que vous voyez ici.',
    download: {
      eyebrow: 'Téléchargement',
      title: 'SiteNorma pour WordPress',
      meta: 'Version {{version}} · {{size}} · nécessite WordPress {{wp}} et PHP {{php}}',
      button: 'Télécharger l’extension',
      checksum: 'Vérifier l’intégrité du fichier',
      unavailable: 'L’extension n’est pas encore disponible au téléchargement.',
    },
    steps: {
      title: 'Comment l’installer',
      one: 'Téléchargez le fichier ZIP.',
      two: 'Dans votre WordPress, allez dans Extensions → Ajouter → Téléverser une extension et choisissez le ZIP.',
      three:
        'Activez l’extension et cliquez sur « Connecter avec SiteNorma ». Vous serez ramené ici pour approuver la connexion.',
      four: 'Votre site apparaît sur la plateforme, sa propriété se vérifie toute seule et l’extension affiche ses résultats.',
    },
    usage: {
      label: 'Sites associés à l’extension',
      of: '{{used}} sites associés sur {{limit}} avec votre formule ({{plan}})',
      unlimited: '{{used}} sites associés (votre formule n’a pas de limite)',
    },
    list: {
      title: 'Sites associés',
      empty: 'Aucun site n’est encore associé à l’extension.',
      tableLabel: 'Sites associés à l’extension (le tableau défile)',
      site: 'Site',
      status: 'Propriété',
      verified: 'Vérifiée',
      unverified: 'Non vérifiée',
      versions: 'Versions',
      versionsValue: 'WordPress {{wp}} · extension {{plugin}}',
      lastSeen: 'Dernière connexion',
      never: 'Aucune pour l’instant',
      linkedBy: 'Associé par',
      actions: 'Actions',
      unlink: 'Dissocier',
      unlinkTitle: 'Dissocier {{name}} ?',
      unlinkBody:
        'L’extension de ce site cessera d’afficher des résultats dès sa prochaine tentative. Le site et son historique restent dans SiteNorma ; vous pourrez l’associer de nouveau quand vous voulez.',
      unlinkConfirm: 'Dissocier',
      cancel: 'Annuler',
      unlinked: '{{name}} a été dissocié.',
    },
    tab: {
      name: 'WordPress',
      title: 'Extension WordPress',
      none: 'Aucune installation WordPress n’est liée à ce site.',
      link: 'Gérer dans Intégrations',
      open: 'Ouvrir dans WordPress',
    },
    connect: {
      title: 'Connecter WordPress à SiteNorma',
      intro:
        'Un WordPress veut s’associer à votre compte. Vérifiez de quel site il s’agit avant d’approuver.',
      site: 'Site à associer',
      organization: 'Organisation',
      account: 'Compte',
      scopeTitle: 'Ce que l’extension pourra faire',
      can1: 'Enregistrer ce site dans votre organisation et vérifier qu’il vous appartient.',
      can2: 'Afficher dans WordPress les résultats de ce site, et uniquement de celui-ci.',
      can3: 'Demander une analyse rapide de ce site, dans les limites de votre formule.',
      cannot:
        'Elle ne pourra pas voir d’autres sites, modifier votre compte ni générer de rapports PDF : cela se fait uniquement d’ici.',
      revoke: 'Vous pouvez la dissocier quand vous voulez depuis cette plateforme.',
      warning: 'N’approuvez que si c’est vous qui avez demandé de connecter {{host}}.',
      approve: 'Approuver la connexion',
      working: 'Connexion…',
      cancel: 'Annuler',
      home: 'Aller au tableau de bord',
      invalid:
        'Le lien de connexion n’est pas valide ou est incomplet. Retournez dans l’extension de votre WordPress et cliquez de nouveau sur « Connecter avec SiteNorma ».',
      errors: {
        generic: 'La connexion n’a pas pu être approuvée. Réessayez dans un instant.',
        permiso:
          'Votre rôle ne permet pas d’associer des sites. Demandez à un éditeur, un administrateur ou un propriétaire de le faire.',
        'limite-webs':
          'Vous avez atteint le maximum de sites associés de votre formule. Dissociez-en un ou changez de formule.',
        callback: 'L’adresse de retour n’appartient pas à ce site : impossible de continuer.',
        url: 'Cette adresse n’est pas publique. Seuls les sites accessibles depuis Internet peuvent être associés.',
        vinculada:
          'Ce site a été associé par une autre personne de l’organisation : seule cette personne ou un administrateur peut l’associer de nouveau.',
        conflicto:
          'Une autre association de ce site était en cours au même moment. Recommencez depuis l’extension.',
        limite: 'Trop de tentatives. Patientez un instant puis réessayez.',
      },
    },
  },
  users: {
    title: 'Utilisateurs',
    intro:
      'Évolution des inscriptions à SiteNorma, comptes créés et formule de chacun. Seuls les super-administrateurs voient cette page.',
    tableLabel: 'Utilisateurs du système (le tableau défile)',
    count_one: '{{count}} utilisateur',
    count_other: '{{count}} utilisateurs',
    columns: {
      user: 'Utilisateur',
      verified: 'E-mail vérifié',
      organizations: 'Organisations',
      signedUp: 'Inscription',
      sites: 'Sites',
      scans: 'Analyses',
      lastSeen: 'Dernière activité',
      status: 'État',
      plan: 'Formule',
    },
    verifiedYes: 'Oui',
    verifiedNo: 'Non',
    noOrganizations: 'Aucune',
    neverSeen: 'Jamais',
    planOf: 'Formule de {{name}}',
    limitMonth: '{{count}} pages par mois',
    limitNone: 'Sans limite',
    infinite: 'Activer la formule infinie',
    infiniteOn: 'Formule infinie activée',
    saved: 'La formule de {{name}} est passée à {{plan}}.',
    error: 'La formule n’a pas pu être modifiée. Réessayez.',
    plans: {
      gratis: 'Gratuite',
      pro: 'Pro',
      agencia: 'Agence',
      'a-medida': 'Sur mesure',
      infinita: 'Infinie',
    },
    wordpress: {
      title: 'Installations WordPress',
      intro:
        'Sites associés à l’extension, tous comptes confondus. Quand on en dissocie un, l’extension cesse d’afficher des résultats dès sa requête suivante ; le site et son historique sont conservés.',
      tableLabel: 'Installations WordPress associées (le tableau défile)',
      empty: 'Aucune installation n’est associée.',
      organization: 'Organisation',
      unlinkBody:
        'Cette installation cessera de fonctionner immédiatement, même si le compte reste actif. L’opération est consignée dans l’historique des modifications.',
    },
    overview: {
      title: 'Inscriptions',
      period: 'Période des graphiques',
      lastDays_one: 'Dernier jour',
      lastDays_other: '{{count}} derniers jours',
    },
    kpi: {
      accounts: 'Comptes au total',
      last7: 'Inscriptions sur 7 jours',
      last30: 'Inscriptions sur 30 jours',
      trend: '{{change}} par rapport aux 7 jours précédents ({{previous}})',
      waitlist: 'Liste d’attente, 30 jours',
      audits: 'Audits gratuits, 30 jours',
      total_one: '{{count}} au total',
      total_other: '{{count}} au total',
      blocked: 'Comptes bloqués',
    },
    chart: {
      signups: 'Inscriptions de comptes par jour',
      signupsColumn: 'Inscriptions',
      waitlist: 'Inscriptions à la liste d’attente par jour',
      waitlistColumn: 'Sur la liste d’attente',
      label: '{{title}}, du {{from}} au {{to}} : {{total}} au total, maximum {{peak}} en un jour.',
      peak: 'Maximum : {{count}} en un jour',
      data: 'Voir les données du graphique',
      day: 'Jour',
    },
    funnel: {
      title: 'Que se passe-t-il après l’inscription',
      intro: 'Comptes ayant atteint chaque étape, sur le total des inscriptions.',
      signedUp: 'Inscription',
      verified: 'E-mail vérifié',
      organization: 'Appartiennent à une organisation',
      site: 'Ont ajouté un site',
      scan: 'Ont lancé une analyse',
      value: '{{count}} · {{percent}} %',
    },
    filters: {
      search: 'Rechercher par nom, e-mail ou organisation',
      show: 'Afficher',
      all: 'Tous les comptes',
      unverified: 'E-mail non vérifié',
      noSite: 'Sans aucun site',
      blocked: 'Bloqués',
      shown: '{{count}} utilisateurs affichés sur {{total}}.',
    },
    active: 'Actif',
    blocked: 'Bloqué',
    administrator: 'Administrateur',
    block: 'Bloquer',
    unblock: 'Débloquer',
    blockTitle: 'Bloquer {{name}} ?',
    blockBody:
      'Cette personne ne pourra plus se connecter et ses sessions ouvertes seront fermées. Ses données ne sont pas supprimées et vous pouvez débloquer le compte quand vous voulez.',
    blockConfirm: 'Bloquer',
    cancel: 'Annuler',
    blockedDone: 'Le compte de {{name}} est bloqué.',
    unblockedDone: '{{name}} peut de nouveau se connecter.',
    blockError: 'L’état du compte n’a pas pu être modifié. Réessayez.',
  },
  members: {
    tableLabel: 'Personnes de l’organisation (le tableau défile)',
    title: 'Organisation : {{name}}',
    membersTitle: 'Personnes',
    name: 'Nom',
    email: 'E-mail',
    role: 'Rôle',
    roleOf: 'Rôle de {{name}}',
    actions: 'Actions',
    you: '(vous)',
    remove: 'Retirer',
    removeOf: 'Retirer {{name}}',
    removeTitle: 'Retirer {{name}} de l’organisation ?',
    removeIntro:
      'Cette personne ne verra plus les sites ni les analyses de l’organisation. Vous pourrez l’inviter de nouveau plus tard.',
    removeConfirm: 'Retirer',
    cancel: 'Annuler',
    roleSaved: 'Le rôle de {{name}} est passé à {{role}}.',
    removed: '{{name}} ne fait plus partie de l’organisation.',
    add: 'Ajouter une personne',
    invitationsTitle: 'Invitations en attente',
    invitationItem: '{{email}} · {{role}} · expire le {{date}}',
    cancelInvitation: 'Annuler l’invitation',
    cancelInvitationOf: 'Annuler l’invitation de {{email}}',
    invitationCancelled: 'Invitation de {{email}} annulée.',
    inviteTitle: 'Inviter une personne',
    inviteIntro:
      'Nous lui enverrons un e-mail contenant un lien pour rejoindre l’organisation. Il expire dans 48 heures.',
    inviteEmail: 'E-mail',
    inviteRole: 'Rôle',
    inviteSubmit: 'Envoyer l’invitation',
    invited: 'Invitation envoyée à {{email}}.',
    roleHints: {
      owner: 'Tout, y compris supprimer l’organisation.',
      admin: 'Tout sauf supprimer l’organisation : sites, analyses et personnes.',
      editor: 'Ajouter, vérifier et analyser des sites, et traiter leurs problèmes.',
      viewer: 'Consultation uniquement.',
    },
    errors: {
      generic: 'Le changement n’a pas pu être effectué. Votre rôle ne le permet peut-être pas.',
      already: 'Cette personne fait déjà partie de l’organisation ou a déjà une invitation.',
    },
  },
  report: {
    download: 'Télécharger le rapport PDF',
    working: 'Préparation du rapport…',
    started: 'Préparation du rapport PDF. Cela peut prendre quelques secondes.',
    ready: 'Rapport téléchargé.',
    note: 'Pour l’instant, le rapport PDF est rédigé en espagnol.',
    errors: {
      informe: 'Le rapport n’a pas pu être préparé. Réessayez dans quelques minutes.',
      limite: 'Vous avez demandé beaucoup de rapports pour ce site. Réessayez dans une heure.',
      'sin-terminar': 'L’analyse n’est pas encore terminée.',
    },
  },
  scans: {
    title: 'Analyses',
    emptyTitle: 'Analysez votre site',
    emptyText: 'Choisissez le type et vérifiez les pages avant de commencer.',
    prepareAgain: 'Nouvelle analyse',
    historyCheck: 'Vérification seule',
    prepare: {
      action: 'Analyser mon site',
      title: 'Préparez votre analyse',
      loadFailed: 'Nous n’avons pas pu préparer le récapitulatif. Rien n’a démarré.',
      profile: 'Profil réglementaire',
      profileUnset:
        'Vous n’avez pas encore choisi de profil réglementaire. Indiquez quelle réglementation sert de référence. Vous pourrez la changer ensuite dans les paramètres du site.',
      scopeTitle: 'Type d’analyse',
      summaryTitle: 'Récapitulatif',
      next: 'Suivant',
      back: 'Précédent',
      modes: {
        pagina: {
          label: 'Une page',
          hint: 'Analyse une page de votre site.',
        },
        rapido: {
          label: 'Analyse rapide',
          hint: 'Analyse jusqu’à {{count}} pages de votre site.',
        },
        completo: {
          label: 'Analyse complète',
          hint: 'Analyse jusqu’à {{count}} pages de votre site.',
        },
      },
      needsOwnership: 'Nécessite de vérifier la propriété du site.',
      verifyAction: 'Vérifier mon site',
      checks:
        'Nous analyserons automatiquement l’accessibilité selon {{standard}}. Certains aspects nécessitent une revue manuelle.',
      methodology: 'Comment nous analysons votre site',
      analysisLimitTitle: 'Limite par analyse',
      analysisLimit_one: 'Jusqu’à {{count}} page avec l’option sélectionnée.',
      analysisLimit_other: 'Jusqu’à {{count}} pages avec l’option sélectionnée.',
      analysisLimitExact_one: 'Utilisera {{count}} page.',
      analysisLimitExact_other: 'Utilisera {{count}} pages.',
      monthlyQuotaTitle: 'Quota mensuel',
      monthlyUsedOf: '{{used}} pages utilisées sur {{limit}}.',
      monthlyAvailable_one: '{{count}} page disponible.',
      monthlyAvailable_other: '{{count}} pages disponibles.',
      monthlyUnlimited: 'Sans limite de pages.',
      monthlyAnalysed_one: '{{count}} page analysée ce mois-ci.',
      monthlyAnalysed_other: '{{count}} pages analysées ce mois-ci.',
      optionOverQuota:
        'La limite par analyse est de {{max}} pages et il vous reste {{remaining}} pages disponibles dans le quota mensuel.',
      pagesHelp: 'Comment nous comptons les pages',
      pages: {
        title: 'Quelles pages voulez-vous analyser ?',
        how: 'Comment elles sont choisies',
        auto: {
          label: 'Sélection automatique',
          hint_one:
            'SiteNorma part de la page d’accueil et choisit 1 page représentative (plan du site et liens de l’accueil).',
          hint_other:
            'SiteNorma part de la page d’accueil et choisit jusqu’à {{count}} pages représentatives (plan du site et liens de l’accueil).',
        },
        manual: {
          label: 'Sélectionner les pages',
          hint_one: 'Choisissez 1 adresse de votre site.',
          hint_other: 'Choisissez jusqu’à {{count}} adresses de votre site.',
          paidOnly: 'Disponible avec les offres payantes.',
        },
        autoOne: 'La page d’accueil du site sera analysée : {{url}}.',
        autoMulti:
          'SiteNorma détectera des pages de votre site et choisira jusqu’à {{count}} des plus représentatives.',
        search: 'Rechercher des pages',
        searchPlaceholder: 'Titre ou adresse',
        listLabel: 'Pages détectées ou déjà analysées',
        chosen: 'Sélectionnées : {{count}} sur {{max}}',
        empty: 'Aucune page ne correspond. Vous pouvez ajouter une adresse ci-dessous.',
        addLabel: 'Ajouter une adresse',
        addHint:
          'Elle doit appartenir au même domaine que le site. Par exemple, /contact ou https://votresite.com/contact',
        addPlaceholder: 'https://votresite.com/contact',
        addAction: 'Ajouter à la sélection',
        addInvalid: 'Saisissez une adresse de ce site, par exemple /contact ou https://votresite.com/contact',
        addDuplicate: 'Cette adresse est déjà dans la sélection.',
        addLimit_one: 'Cette analyse admet au plus {{count}} page.',
        addLimit_other: 'Cette analyse admet au plus {{count}} pages.',
        summaryTitle: 'Résumé de la sélection',
        summaryCount_one: '{{count}} page sélectionnée :',
        summaryCount_other: '{{count}} pages sélectionnées :',
      },
      noPages: {
        title: 'Il ne vous reste aucune page disponible',
        text: 'Vous avez utilisé le quota mensuel de ce mois-ci. Le compteur se réinitialise le {{date}}.',
      },
      insufficient:
        'Vous n’avez pas assez de pages dans le quota mensuel : la limite par analyse est de {{planned}} et il vous reste {{remaining}} pages disponibles. Le compteur se réinitialise le {{date}}.',
      useOnePage: 'Analyser une seule page',
      seePlans: 'Voir les offres',
      check: {
        mode: 'Vérifier la correction',
        modeHint_one:
          'Uniquement la page où se trouve la correction que vous voulez vérifier, sur ordinateur et sur mobile.',
        modeHint_other:
          'Uniquement les {{count}} pages où se trouvent les corrections que vous voulez vérifier, sur ordinateur et sur mobile.',
        title: 'Ce qui sera vérifié',
        intro:
          'C’est une vérification de corrections, pas une analyse de tout votre site : elle n’examine que les pages de ces éléments et ne cherchera pas de problèmes ailleurs.',
        elements_one: '{{count}} élément',
        elements_other: '{{count}} éléments',
        pagesTitle: 'Pages qui seront analysées',
        devices: 'Appareils : {{list}}.',
        conditionsTitle: 'Dans quelles conditions',
        conditions:
          'Chaque page est chargée comme la voit un nouveau visiteur, sans session ouverte, sur ordinateur et sur mobile. Si un élément n’apparaît que dans une fenêtre contextuelle ou après connexion, le résultat sera « Non confirmé automatiquement » ou « Revue manuelle requise », jamais « corrigé ».',
        scope_one: 'Périmètre : {{count}} page parmi celles de votre site.',
        scope_other: 'Périmètre : {{count}} pages parmi celles de votre site.',
        skipped_one: '{{count}} élément ne sera pas vérifié (il est archivé ou écarté).',
        skipped_other: '{{count}} éléments ne seront pas vérifiés (ils sont archivés ou écartés).',
        tooMany:
          'Il y a des corrections sur {{count}} pages et une vérification peut en examiner {{max}} au maximum. Vérifiez moins d’éléments à la fois.',
        nothing: 'Il n’y a rien à vérifier avec les éléments choisis.',
        other: 'Autres analyses',
        result:
          'À la fin, chaque élément affichera son résultat : « Toujours détecté », « Non détecté lors de la dernière vérification », « Non confirmé automatiquement » ou « Revue manuelle requise ». Seule une vérification comparable qui ne le détecte pas permet d’archiver le travail.',
      },
      start: 'Lancer l’analyse',
      startAuto_one: 'Analyser 1 page',
      startAuto_other: 'Analyser jusqu’à {{count}} pages',
      startExact_one: 'Analyser 1 page',
      startExact_other: 'Analyser {{count}} pages',
      starting: 'Lancement de l’analyse…',
      cancel: 'Annuler',
      startFailed:
        'Nous n’avons pas pu lancer l’analyse. Aucune page n’a été utilisée : vous pouvez réessayer.',
    },
    errors: {
      'en-curso': 'Une analyse est déjà en cours pour ce site.',
      verificacion: 'Pour l’analyse complète, la propriété du site doit être vérifiée.',
      limite: 'Vous avez lancé beaucoup d’analyses aujourd’hui. Réessayez demain.',
      saldo: 'Il ne vous reste pas assez de pages ce mois-ci pour cette analyse. Rien n’a démarré.',
      permiso: 'Votre rôle ne permet pas d’analyser des sites.',
      plan: 'Choisir les pages à la main est disponible avec les offres payantes. Rien n’a démarré.',
      urls: 'Une adresse n’est pas valide, est en double ou n’appartient pas à ce site. Rien n’a démarré.',
      'limite-paginas': 'Vous avez choisi plus de pages que ce type d’analyse n’admet. Rien n’a démarré.',
      'nada-que-comprobar':
        'Il n’y a rien à vérifier : les éléments choisis sont archivés, écartés ou n’existent plus.',
      'demasiadas-paginas': 'Ces corrections se trouvent sur trop de pages pour une seule vérification.',
      'no-completable': 'Seule une analyse complète terminée partiellement peut être complétée.',
      'nada-que-completar': 'Il ne reste aucune page en attente à compléter dans cette analyse.',
    },
    types: {
      pagina: 'Une page',
      rapido: 'Rapide',
      completo: 'Complète',
      personalizado: 'Personnalisée',
      comprobacion: 'Vérification des corrections',
    },
    latestTitle: 'Dernière analyse',
    lastAvailableTitle: 'Dernier résultat disponible',
    meta: '{{date}} · {{type}}',
    scoreNote: 'Ne correspond pas à la conformité légale.',
    summary: {
      kinds: {
        rapido: 'rapide',
        completo: 'complète',
        personalizado: 'personnalisée',
        comprobacion: 'de vérification',
      },
      pages_one: '{{count}} page',
      pages_other: '{{count}} pages',
      found_one: 'L’analyse {{kind}} de {{pages}} a détecté {{count}} problème d’accessibilité.',
      found_other: 'L’analyse {{kind}} de {{pages}} a détecté {{count}} problèmes d’accessibilité.',
      none: 'L’analyse {{kind}} de {{pages}} n’a détecté aucun problème d’accessibilité avec les tests automatiques.',
      nothing: 'L’analyse {{kind}} n’a pu analyser aucune page.',
      pendingReview: 'Des vérifications sont en attente de revue manuelle.',
    },
    noScore: 'Sans estimation : aucune page n’a pu être analysée.',
    blocked:
      'Le site a bloqué l’analyse automatique sur {{count}} page(s). Si la cause est son fichier robots.txt, vérifiez la propriété du site (les analyses des sites vérifiés n’en dépendent pas) ou autorisez SiteNormaBot dans ce fichier.',
    limited:
      'Le site a limité les requêtes de SiteNorma sur {{count}} page(s) : elles n’ont pas été analysées et ne comptent pas dans l’estimation. Ce n’est pas un problème du site.',
    partial:
      'Sur {{count}} page(s), le site a limité les requêtes : l’analyse a porté sur la page chargée, mais la capture peut montrer des images non chargées. Nous ne comptons pas ces refus comme des problèmes.',
    details: 'Voir le rapport complet',
    history: 'Analyses précédentes',
    historyItem: '{{date}} · {{type}}',
    estimate: 'Estimation automatique d’accessibilité',
    bands: {
      excelente: 'Excellent',
      bueno: 'Bon',
      mejorable: 'À améliorer',
      deficiente: 'Insuffisant',
      'revision-pendiente': 'Revue manuelle en attente',
      'sin-datos': 'Sans données',
    },
    modules: {
      seo: 'SEO',
      quality: 'Qualité',
    },
    allPages: 'Voir les {{count}} pages',
    pagesHeading: 'Pages analysées',
    about: 'À propos de cette analyse',
    seeAllProblems: 'Voir les problèmes',
    trend: 'Évolution de l’estimation',
    historyScore: 'Estimation : {{score}}/100',
  },
  scan: {
    title: 'Analyse du {{date}}',
    technical: 'Détails techniques',
    back: 'Retour à Analyses',
    pageBlocked: 'Bloquée : {{reason}}',
    pageError: 'En erreur : {{error}}',
    issuesTitle: 'Problèmes',
    groups: {
      violations: 'Problèmes WCAG',
      review: 'Nécessitent une revue manuelle',
      bestPractices: 'Bonnes pratiques',
      seo: 'SEO',
      quality: 'Qualité',
    },
    empty: 'Aucun.',
    severity: {
      critica: 'Critique',
      grave: 'Grave',
      moderada: 'Modéré',
      leve: 'Mineur',
    },
    criteria: 'WCAG {{list}}',
    devices: {
      escritorio: 'ordinateur',
      movil: 'mobile',
    },
    where: 'Sur {{pages}} page(s) · {{nodes}} élément(s) · {{devices}}',
    fix: 'Comment le corriger',
    examples: 'Exemples',
    exampleOn: 'Sur {{url}} ({{device}})',
    engineScore:
      'Sans compter les détections que votre équipe a écartées avec justification. Le moteur a donné {{score}}/100.',
    discardedNote_one: '{{count}} détection écartée avec justification n’est pas comptée.',
    discardedNote_other: '{{count}} détections écartées avec justification ne sont pas comptées.',
    discardedMethod: 'Comment s’appliquent les détections écartées',
    groupCount_one: '({{count}} problème · {{rules}} règle)',
    groupCount_other: '({{count}} problèmes · {{rules}} règles)',
    rule: {
      variant: 'Variante {{index}} sur {{total}}',
      where: 'Sur {{pages}} page(s) · {{nodes}} élément(s)',
      wherePages: 'Sur {{pages}} page(s)',
      onPage_one: '{{count}} élément sur cette page',
      onPage_other: '{{count}} éléments sur cette page',
      onPageAtLeast_one: 'Au moins {{count}} élément sur cette page',
      onPageAtLeast_other: 'Au moins {{count}} éléments sur cette page',
      elsewhere_one: 'aussi sur {{count}} autre page',
      elsewhere_other: 'aussi sur {{count}} autres pages',
      thisPageAndMore_one: 'Sur cette page et sur {{count}} autre',
      thisPageAndMore_other: 'Sur cette page et sur {{count}} autres',
      thisPage: 'Sur cette page',
      variants_one: '{{count}} variante',
      variants_other: '{{count}} variantes',
    },
    elementsOnPage_one: 'Son élément sur cette page',
    elementsOnPage_other: 'Ses {{count}} éléments sur cette page',
    charts: {
      severity: 'Problèmes par gravité',
      principles: 'Problèmes par principe des WCAG',
      top: 'Les problèmes les plus fréquents',
      topHint: 'Nombre de pages où chacun apparaît.',
      pageSeverity: 'Problèmes de cette page',
    },
    principles: {
      '1': 'Perceptible',
      '2': 'Utilisable',
      '3': 'Compréhensible',
      '4': 'Robuste',
    },
    map: {
      title: 'Carte des pages',
      hint: 'Chaque page, avec sa capture, prend la couleur de son problème le plus grave. Appuyez dessus pour voir ses données.',
      noCaptures:
        'Les captures de cette analyse ne sont plus disponibles : seules celles des deux dernières analyses de chaque site sont conservées.',
    },
    filters: {
      severity: 'Filtrer par gravité',
      search: 'Rechercher un problème',
      results_one: '{{count}} problème',
      results_other: '{{count}} problèmes',
    },
    pages: {
      empty: 'Aucune page n’a encore été analysée. Commencez par une analyse depuis Analyses.',
      notReady: 'Cette analyse n’est pas encore terminée : ses pages apparaîtront à la fin.',
      failed:
        'Cette analyse n’a pas pu être menée à son terme : il n’y a donc aucune page analysée à afficher.',
      none: 'Cette analyse ne comporte aucune page.',
      list: 'Pages de l’analyse',
      sort: {
        label: 'Trier les pages',
        prioridad: 'Par priorité',
        nombre: 'Par nom',
        pendientes: 'Par problèmes en attente',
        fecha: 'Par date de la dernière analyse',
      },
      search: 'Rechercher une page',
      found: 'Pages trouvées : {{count}}',
      newTab: '(s’ouvre dans un nouvel onglet)',
      elements_one: '{{count}} élément concerné',
      elements_other: '{{count}} éléments concernés',
      review_one: '{{count}} à vérifier',
      review_other: '{{count}} à vérifier',
      issues_one: 'Problèmes de cette page ({{count}})',
      issues_other: 'Problèmes de cette page ({{count}})',
      issuesHint:
        'Regroupés par règle. Ouvrez-en un pour voir comment le corriger et ses éléments ; « Repérer » les marque sur la capture.',
      clean:
        'Nous n’avons détecté aucun problème sur cette page. Les tests automatiques ne vérifient pas tout : la revue manuelle reste à faire.',
    },
    facts: {
      status: 'Réponse',
      load: 'Chargement',
      ttfb: 'Premier octet',
      bytes: 'Poids',
      requests: 'Requêtes',
      lang: 'Langue',
      features: 'Contient : {{list}}.',
    },
    features: {
      forms: 'formulaires',
      video: 'vidéo',
      audio: 'audio',
      iframes: 'iframes',
    },
    pageBlockedShort: 'Bloquée',
    pageErrorShort: 'En erreur',
    elementOn: 'Élément {{number}} ({{device}})',
  },
  viz: {
    ring: {
      none: 'Sans estimation : aucune page n’a pu être analysée.',
      pendingHint:
        'Les {{pending}} points en attente s’ajoutent après la revue manuelle. Jusque-là, l’estimation ne peut pas dépasser {{max}} sur 100.',
      full: 'Estimation automatique : {{score}} sur 100. {{earned}} obtenus, {{lost}} perdus et {{pending}} en attente de revue manuelle.',
      short: 'Estimation automatique : {{score}} sur 100.',
      parts: {
        earned: 'Points obtenus',
        lost: 'Perdus',
        manual: 'Revue manuelle',
        pending: 'En attente de revue manuelle',
      },
    },
    points_one: '{{count}} point',
    points_other: '{{count}} points',
    severities: {
      critica: 'Critiques',
      grave: 'Graves',
      moderada: 'Modérés',
      leve: 'Mineurs',
    },
    review: 'À vérifier',
    chips: {
      ok: 'Aucun problème détecté',
      review_one: '{{count}} à vérifier',
      review_other: '{{count}} à vérifier',
      critica_one: '{{count}} problème critique',
      critica_other: '{{count}} problèmes critiques',
      grave_one: '{{count}} problème grave',
      grave_other: '{{count}} problèmes graves',
      moderada_one: '{{count}} problème modéré',
      moderada_other: '{{count}} problèmes modérés',
      leve_one: '{{count}} problème mineur',
      leve_other: '{{count}} problèmes mineurs',
    },
    module: {
      label: '{{label}} : {{score}} sur 100',
      none: '{{label}} : sans données',
    },
    trend: {
      label_one: 'Évolution de l’estimation sur {{count}} analyse : {{to}} ({{end}}).',
      label_other:
        'Évolution de l’estimation sur {{count}} analyses : de {{from}} ({{start}}) à {{to}} ({{end}}).',
    },
  },
  live: {
    mosaic: 'Pages de l’analyse',
    analysing: 'Analyse en cours…',
    analysed: 'Analysée',
    waiting: 'En file d’attente',
    moreWaiting_one: 'et {{count}} autre en file d’attente',
    moreWaiting_other: 'et {{count}} autres en file d’attente',
    blocked: 'Bloquée',
    error: 'En erreur',
  },
  results: {
    backToResults: 'Retour aux résultats de l’analyse',
    backToPages: 'Retour à toutes les pages',
    summaries: 'Résumés de l’analyse',
    pagesCount_one: '{{count}} page',
    pagesCount_other: '{{count}} pages',
    priority: {
      alta: 'Priorité haute',
      media: 'Priorité moyenne',
      baja: 'Priorité basse',
      pendiente: 'Priorité à vérifier',
    },
    priorityWhy: {
      alta: 'Peut empêcher certaines personnes d’utiliser cette partie du site.',
      media:
        'Rend l’utilisation plus difficile pour certaines personnes, même s’il existe généralement un autre moyen de continuer.',
      baja: 'C’est une gêne mineure ou une amélioration recommandée.',
      pendiente:
        'L’analyse automatique n’a pas pu décider s’il s’agit d’un problème : une personne doit le vérifier.',
    },
    priorityWhyOther: {
      alta: 'C’est important pour le fonctionnement ou la visibilité du site. Ce n’est pas un problème d’accessibilité.',
      media:
        'Il est conseillé de le corriger, mais ce n’est pas urgent. Ce n’est pas un problème d’accessibilité.',
      baja: 'C’est une amélioration mineure. Ce n’est pas un problème d’accessibilité.',
    },
    priorityWhyPractice: {
      alta: 'C’est une bonne pratique importante pour les personnes qui utilisent des technologies d’assistance, même si ce n’est pas un manquement direct aux WCAG.',
      media: 'C’est une bonne pratique recommandée, même si ce n’est pas un manquement direct aux WCAG.',
      baja: 'C’est une amélioration mineure de bonne pratique, pas un manquement aux WCAG.',
    },
    why: 'Pourquoi :',
    affects: 'Touche surtout les personnes ayant : {{who}}.',
    origin: {
      automatic: 'Détecté automatiquement',
      review: 'Détection automatique · à vérifier manuellement',
      manual: 'Détecté lors d’une revue manuelle',
    },
    actions: {
      fix: 'Voir et corriger',
      check: 'Voir et vérifier',
    },
    card: {
      elements_one: '{{count}} élément concerné',
      elements_other: '{{count}} éléments concernés',
      elementsAtLeast_one: '{{count}}+ éléments concernés',
      elementsAtLeast_other: '{{count}}+ éléments concernés',
      review_one: '{{count}} élément à vérifier',
      review_other: '{{count}} éléments à vérifier',
      reviewAtLeast_one: '{{count}}+ éléments à vérifier',
      reviewAtLeast_other: '{{count}}+ éléments à vérifier',
    },
    more: {
      title: 'Autres vérifications',
      show: 'Afficher',
    },
    issue: {
      problem: 'Problème',
      noPageData: 'Il n’y a pas de données de cette page pour ce problème.',
      pageOf_one: 'Page concernée',
      pageOf_other: 'Concerne {{count}} pages : choisissez-en une',
      fix: 'Comment le corriger',
      check: 'Comment le vérifier',
      wcag: 'Référence WCAG',
      elements: 'Éléments concernés',
      pages: 'Pages concernées',
      technical: 'Détails techniques',
      moreElements: 'Et {{count}} éléments de plus. Vous les trouverez dans les pages concernées.',
      seeInPage: 'Voir sur la page',
      sharedOne: 'Ce problème touche {{pages}} pages et se trouve dans un composant partagé.',
      sharedMany: 'Ce problème touche {{pages}} pages et se trouve dans {{components}} composants partagés.',
      seePages_one: 'Voir la page concernée',
      seePages_other: 'Voir les {{count}} pages concernées',
      sharedSelector: 'Sélecteur du composant partagé',
      area: 'Domaine',
      severity: 'Gravité',
      type: 'Type',
      showing: '{{shown}} sur {{total}}',
      areas: { todas: 'Tous', bestPractices: 'Bonnes pratiques', seo: 'SEO', quality: 'Qualité' },
      tracking: {
        title: 'Suivi de ce problème',
        hint: 'Travail, validation et vérification',
        none: 'Sans suivi',
        validation: 'Confirmées : {{confirmed}} · Écartées : {{discarded}}',
        noDecisions: 'Aucune décision',
        verificationTitle: 'Vérification de la correction',
        verification: 'Vérifiés : {{verified}} · Toujours détectés : {{persists}} · En attente : {{pending}}',
      },
    },
    shared: {
      chip: 'Problème global',
      title: 'Éléments partagés',
      intro:
        "Le même élément, avec la même structure, apparaît sur plusieurs pages : un composant partagé (en-tête, pied de page, menu, formulaire…) en est la cause. C'est un seul problème, géré depuis un seul endroit et corrigé une seule fois.",
      zone: 'Dans {{zone}}',
      pages_one: 'Sur {{count}} page',
      pages_other: 'Sur {{count}} pages',
      identical: 'Le code est identique partout.',
      textChanges:
        "Même structure ; le texte ou les adresses changent d'une page à l'autre (par exemple, entre les langues).",
      seePages: 'Voir les pages',
      separately:
        "Il est suivi et corrigé depuis un seul endroit : ce que vous indiquez s'applique à toutes les pages, mais chacune n'est considérée comme vérifiée que lorsqu'une nouvelle analyse ne le détecte plus.",
      owner: 'Géré depuis {{page}}',
      ownerGlobal: 'Géré depuis la section des problèmes globaux',
      elementIn_other: 'Élément partagé sur {{count}} pages',
      alsoHere_one: 'Un autre problème global touche aussi cette page ; il est géré ailleurs.',
      alsoHere_other: '{{count}} problèmes globaux touchent aussi cette page ; ils sont gérés ailleurs.',
    },
    globals: {
      title: 'Problèmes globaux',
      intro:
        "Ils touchent des éléments partagés par plusieurs pages (en-tête, pied de page, menu…). Ils s'affichent une seule fois et sont gérés depuis un seul endroit.",
      manage: 'Voir et gérer',
      where: 'Géré sur {{page}}',
      whereGlobal: "La page d'accueil ne l'a pas : géré sur {{page}}",
    },
    counts: {
      pageOnly: 'Concerne cette page',
      pagesOnly: 'Concerne {{pages}}',
      elementsOnly_one: '{{count}} élément',
      elementsOnly_other: '{{count}} éléments',
      elementsOnlyAtLeast_one: 'au moins {{count}} élément',
      elementsOnlyAtLeast_other: 'au moins {{count}} éléments',
      affectsPage: 'concerne la page',
      languages_one: '{{count}} langue : {{list}}',
      languages_other: '{{count}} langues : {{list}}',
    },
    devices: {
      both: 'sur ordinateur et sur mobile',
      desktop: 'uniquement sur ordinateur',
      mobile: 'uniquement sur mobile',
    },
    manual: {
      figures: { registered: 'Révisées', pending: 'En attente', decide: 'Éléments à trancher' },
      open: 'Voir les vérifications manuelles',
      title: 'Vérifications manuelles',
      progress: '{{done}} sur {{total}} critères avec au moins une revue enregistrée',
      pending_one: '{{count}} critère en attente de revue manuelle.',
      pending_other: '{{count}} critères en attente de revue manuelle.',
      itemsDecided:
        'Sur {{total}} éléments que l’analyse n’a pas pu trancher, votre équipe en a confirmé ou écarté {{decided}}.',
      hint: 'Les tests automatiques ne remplacent pas la revue manuelle : aucun critère n’est considéré comme respecté tant qu’une personne ne l’a pas examiné.',
      intro:
        'La revue manuelle vérifie, critère par critère, ce qu’aucun outil automatique ne peut trancher. Cette liste reprend les critères de la norme de votre site et indique où en est chacun : le résultat de l’analyse automatique et celui de la revue manuelle sont séparés.',
      status: {
        incumple: 'Non respecté (détecté)',
        'sin-incidencias': 'Aucun problème détecté automatiquement : revue manuelle requise',
        pendiente: 'En attente de revue manuelle',
      },
      statusHelp: {
        incumple: 'L’analyse automatique a trouvé au moins un problème qui concerne ce critère.',
        'sin-incidencias':
          'Les vérifications automatiques applicables n’ont trouvé aucun défaut, mais cela ne suffit pas : il faut le vérifier à la main.',
        pendiente:
          'Aucun test automatique ne l’a vérifié sur ces pages, ou il ne peut pas être vérifié avec des outils automatiques.',
      },
      toReview_one: '{{count}} point à trancher à la main',
      toReview_other: '{{count}} points à trancher à la main',
    },
    summary: {
      globalProblems_one: '1 concerne plusieurs pages',
      globalProblems_other: '{{count}} concernent plusieurs pages',
      title: 'Résumé',
      priority: 'Priorité',
      priorities: {
        alta: '{{count}} de priorité haute',
        media: '{{count}} de priorité moyenne',
        baja: '{{count}} de priorité basse',
      },
      problems: 'Types de problème',
      problemsHelp: 'Ce qu’est un type de problème',
      problemsHelpText:
        'Une même catégorie de défaut, même si elle se répète sur plusieurs pages ou éléments.',
      elements: 'Éléments concernés',
      elementsHelp: 'Ce qu’est un élément concerné',
      elementsHelpText:
        'Une image, un bouton ou un autre élément en échec. S’il échoue sur ordinateur et sur mobile, il compte une fois.',
      atLeast: 'Au moins',
      pages: 'Pages avec des problèmes',
      sharedElements: '{{count}} se répètent sur d’autres pages',
      pagesOfTotal: '{{count}} sur {{total}} analysées',
      repeated:
        'Un même élément peut échouer dans plusieurs types de problème, mais il n’est compté qu’une fois dans le total. C’est pourquoi la somme donne {{summed}} et le total est {{total}}.',
      none_one: 'Aucun problème automatique détecté sur la page analysée.',
      none_other: 'Aucun problème automatique détecté sur les {{count}} pages analysées.',
      how: {
        title: 'Comment ces chiffres sont comptés',
        problem: {
          term: 'Type de problème',
          text: 'Une même catégorie de défaut (par exemple, « Image sans texte alternatif »), même si elle se répète.',
        },
        element: {
          term: 'Élément concerné',
          text: 'Une image, un bouton ou un autre élément en échec. S’il échoue sur ordinateur et sur mobile, il compte une fois.',
        },
        atLeast: {
          term: 'Le signe « + »',
          text: 'L’analyse cesse de compter lorsqu’il y a beaucoup d’éléments sur une page ; le total réel peut être plus élevé.',
        },
        appearance: {
          term: 'Occurrence',
          text: 'Chaque fois qu’une vérification trouve un élément (par appareil). Visible dans le détail technique.',
        },
        devices: {
          term: 'Appareils',
          text: 'Les chiffres additionnent ordinateur et mobile, sauf si un filtre indique autre chose.',
        },
        pages: {
          term: 'Pages avec des problèmes',
          text: 'Pages de cette analyse avec au moins un type de problème. L’onglet « Pages » liste toutes celles analysées un jour.',
        },
        pageCount: {
          term: 'Le nombre sur chaque page',
          text: 'Types de problème distincts sur cette page. La somme des pages peut dépasser le total de l’analyse.',
        },
        repeated: {
          term: 'Un élément dans plusieurs types',
        },
      },
    },
    pageSummary: {
      global_one: 'dont 1 global',
      global_other: 'dont {{count}} globaux',
      title: 'Résumé de cette page',
      none: 'L’analyse automatique n’a détecté aucun problème d’accessibilité sur cette page. Cela ne signifie pas qu’elle soit totalement accessible.',
      problems_one: '{{count}} problème détecté',
      problems_other: '{{count}} problèmes détectés',
      elements_one: '{{count}}{{plus}} élément concerné',
      elements_other: '{{count}}{{plus}} éléments concernés',
      priorities: {
        alta: 'Priorité haute : {{count}}',
        media: 'Priorité moyenne : {{count}}',
        baja: 'Priorité basse : {{count}}',
      },
    },
    start: {
      title: 'Par où commencer',
      intro:
        'Commencez par ces problèmes : ce sont ceux de priorité la plus haute et, à priorité égale, ceux qui concernent le plus d’éléments et de pages.',
      counts_one: '{{count}} élément concerné sur {{pages}}',
      counts_other: '{{count}} éléments concernés sur {{pages}}',
      countsAtLeast_one: 'Au moins {{count}} élément concerné sur {{pages}}',
      countsAtLeast_other: 'Au moins {{count}} éléments concernés sur {{pages}}',
      how: {
        title: 'Comment la priorité est-elle déterminée ?',
        intro:
          'La priorité indique à quel point un problème gêne l’utilisation du site, selon l’impact que relève le test. Elle ne mesure pas le risque juridique.',
        alta: 'Il peut empêcher quelqu’un de mener à bien une tâche (par exemple, un bouton que le lecteur d’écran ne sait pas annoncer).',
        media:
          'Il rend l’utilisation plus difficile, mais il existe généralement un autre moyen de continuer.',
        baja: 'Une gêne mineure ou une amélioration recommandée.',
        pendiente: 'L’analyse ne peut pas décider s’il s’agit d’un problème : une personne doit le vérifier.',
        order:
          'Au sein de chaque priorité, ceux qui concernent le plus d’éléments passent en premier, puis ceux qui concernent le plus de pages et, enfin, l’ordre alphabétique.',
      },
    },
    score: {
      title: 'Estimation automatique',
      value: '{{score}}/100',
      intro: 'De 0 à 100 points. Ce n’est ni un pourcentage de conformité WCAG ni la conformité légale.',
      method: 'Comment elle est calculée',
    },
    honesty: {
      text: 'L’analyse automatique détecte une partie des problèmes. Certains aspects exigent une revue manuelle.',
      link: 'Comment nous analysons votre site',
    },
    pageCard: {
      onlyGlobal_one: '{{count}} problème global',
      onlyGlobal_other: '{{count}} problèmes globaux',
      languages: 'Versions par langue',
      showLanguage: 'Voir la version en {{language}}',
      problems_one: '{{count}} problème',
      problems_other: '{{count}} problèmes',
      none: 'Aucun problème détecté',
      noneShort: 'Aucun problème',
    },
    context: {
      label: 'Ce qui a été analysé',
      date: 'Analyse du {{date}}',
      devices: 'Appareils : ordinateur et mobile',
      complete: 'Analyse complète',
      partial_one: 'Analyse partielle : il reste {{count}} page à analyser',
      partial_other: 'Analyse partielle : il reste {{count}} pages à analyser',
      previous: 'Résultat précédent',
    },
    previous: {
      title: 'Ceci est un résultat précédent.',
      text: 'Une analyse plus récente existe, celle du {{date}}.',
      link: 'Voir l’analyse la plus récente',
    },
    tabs: {
      label: 'Catégories de résultats',
      problems: 'Problèmes détectés',
      passed: 'Vérifications sans problème détecté',
      unchecked: 'Points qui n’ont pas pu être vérifiés',
      manual: 'Vérifications manuelles',
      other: 'Bonnes pratiques, SEO et qualité',
    },
    problems: {
      none: {
        scan: 'L’analyse automatique n’a détecté aucun problème d’accessibilité sur les pages analysées. Cela ne signifie pas que le site soit totalement accessible : les vérifications manuelles restent à faire.',
        page: 'L’analyse automatique n’a détecté aucun problème d’accessibilité sur cette page. Cela ne signifie pas qu’elle soit totalement accessible : les vérifications manuelles restent à faire.',
      },
    },
    passed: {
      level: 'Niveau {{level}}',
      result: 'Résultat :',
      resultPage: 'La vérification automatique n’a trouvé aucun problème sur cette page.',
      resultPages_one: 'La vérification automatique n’a trouvé aucun problème sur {{count}} page.',
      resultPages_other: 'La vérification automatique n’a trouvé aucun problème sur {{count}} pages.',
      where:
        'Elle examine la page entière : l’analyse ne conserve pas les éléments précis vérifiés, ils ne peuvent donc pas être signalés sur la capture. Il n’y a rien à corriger ici, et cela ne signifie pas que le critère est entièrement respecté : une revue manuelle est nécessaire.',
      intro: {
        scan: 'Vérifications automatiques appliquées aux pages analysées qui n’ont relevé aucun défaut.',
        page: 'Vérifications automatiques appliquées à cette page qui n’ont relevé aucun défaut.',
      },
      note: 'Qu’une vérification ne relève aucun défaut ne signifie pas que le critère est respecté : les tests automatiques ne détectent qu’une partie des obstacles et les critères restent à examiner lors d’une revue manuelle.',
      unavailable:
        'Cette analyse a été réalisée avant que nous enregistrions cette information. Lancez une nouvelle analyse pour voir les vérifications sans problème détecté.',
      none: 'Aucune vérification n’a pu être appliquée sans relever de problème.',
      wcag_one: '{{count}} vérification des règles WCAG',
      wcag_other: '{{count}} vérifications des règles WCAG',
      practices_one: '{{count}} vérification de bonnes pratiques',
      practices_other: '{{count}} vérifications de bonnes pratiques',
      inPages_one: 'sur {{count}} page',
      inPages_other: 'sur {{count}} pages',
    },
    unchecked: {
      intro:
        'Voici ce que l’analyse automatique n’a pas pu trancher et ce qui n’a pas été analysé. Rien de tout cela n’est considéré comme correct.',
      byHand: 'À trancher à la main ({{count}})',
      byHandHint:
        'L’analyse automatique n’a pas pu décider s’il s’agit d’un problème. Une personne doit le vérifier et, si elle le souhaite, enregistrer sa décision.',
      noneByHand: 'Aucun point n’attend d’être tranché à la main.',
      pages: 'Pages qui n’ont pas été analysées ({{count}})',
      pagesHint: 'Ces pages n’ont pas été analysées ; elles ne comptent donc pas comme validées.',
      noPages: 'Toutes les pages prévues ont été analysées.',
    },
    other: {
      intro:
        'Vérifications qui aident à améliorer le site mais ne sont pas des problèmes d’accessibilité au sens des WCAG.',
      note: 'Les bonnes pratiques, le SEO et la qualité technique ne comptent pas dans le résumé des problèmes d’accessibilité.',
      bestPractices: 'Bonnes pratiques d’accessibilité',
      bestPracticesHint:
        'Elles améliorent l’expérience avec les technologies d’assistance, mais ne contreviennent pas directement à un critère des WCAG.',
      seo: 'SEO (visibilité dans les moteurs de recherche)',
      seoHint: 'Elles aident les moteurs de recherche à trouver et à comprendre le site.',
      quality: 'Qualité technique',
      qualityHint: 'Erreurs techniques, sécurité et cookies de la page.',
      empty: 'Rien à afficher dans cette section.',
    },
    filters: {
      title: 'Filtrer',
      priority: 'Priorité',
      device: 'Appareil',
      devices: {
        todos: 'Tous les appareils',
        escritorio: 'Ordinateur',
        movil: 'Mobile',
      },
      search: 'Rechercher par nom, explication ou critère WCAG',
      shown: 'Affichage de {{shown}} sur {{total}}.',
      clear: 'Retirer les filtres',
      noMatch:
        'Aucun résultat ne correspond aux filtres. Appuyez sur « Retirer les filtres » pour tout voir.',
    },
    detail: {
      title: 'Résultats détaillés',
      howToCheck: 'Comment le vérifier',
      where: 'Où cela se trouve',
      examples: 'Exemples d’éléments',
      engineMessage: 'Message technique du moteur',
      elementsHere_one: 'L’élément de cette page',
      elementsHere_other: 'Les {{count}} éléments de cette page',
      technical: 'Détail technique',
      rule: 'Règle du moteur',
      criteria: 'Critères WCAG associés',
      criterion: 'WCAG {{id}} {{title}} (niveau {{level}})',
      appearances: 'Occurrences',
      appearancesValue_one: '{{count}} occurrence',
      appearancesValue_other: '{{count}} occurrences',
      appearancesHelp:
        'Chaque fois que la vérification trouve un élément, cela compte pour une occurrence : un même élément en défaut sur ordinateur et sur mobile totalise deux occurrences, mais reste un seul élément.',
      groups: 'Regroupements techniques',
      groupsValue_one: '{{count}} groupe',
      groupsValue_other: '{{count}} groupes',
      groupsHelp:
        'Éléments de la même règle regroupés parce qu’ils sont du même type et se trouvent dans la même structure de page, un lien que vérifie le moteur. Chaque groupe a son propre suivi.',
      scopeNote: 'Ce qui est compté ici',
      scopeNoteValue: 'Éléments distincts de toutes les pages analysées, ordinateur et mobile réunis.',
    },
  },
  status: {
    outcomes: {
      queued: 'Analyse en file d’attente',
      running: 'Analyse en cours',
      complete: 'Analyse terminée',
      partial: 'Analyse partiellement terminée',
      failed: 'L’analyse n’a pas pu être menée à bien',
    },
    announce: {
      queued: 'Analyse en file d’attente.',
      running: 'Analyse en cours.',
      complete: 'Analyse terminée. Vous pouvez consulter les résultats.',
      partial: 'Analyse partiellement terminée. Vérifiez quelles pages n’ont pas été analysées.',
      failed: 'L’analyse n’a pas pu être menée à bien.',
      refreshed: 'État mis à jour.',
    },
    identity: {
      url: 'Adresse',
      scope: 'Périmètre',
      scopeValue_one: '{{mode}} · jusqu’à {{count}} page, sur ordinateur et sur mobile',
      scopeValue_other: '{{mode}} · jusqu’à {{count}} pages, sur ordinateur et sur mobile',
      requested: 'Demandée le',
    },
    modes: {
      pagina: 'Uniquement la page indiquée',
      rapido: 'Analyse rapide',
      completo: 'Analyse complète',
      personalizado: 'Analyse personnalisée',
      comprobacion: 'Vérification des corrections',
    },
    queued: {
      text: 'Votre analyse est en file d’attente. Elle commencera dès qu’il y aura de la disponibilité.',
    },
    leave:
      'Vous pouvez quitter cet écran. L’analyse continuera et vous pourrez consulter son état depuis le site.',
    progress: 'Analyse de {{done}}/{{total}}',
    progressEta: {
      seconds: 'Environ {{count}} secondes restantes',
      minutes_one: 'Environ {{count}} minute restante',
      minutes_other: 'Environ {{count}} minutes restantes',
    },
    phases: {
      title: 'Étapes de l’analyse',
      queued: 'En file d’attente',
      preparing: 'Préparation de l’analyse',
      analysing: 'Analyse des pages',
      finishing: 'Calcul de l’estimation',
      done: 'terminée',
      current: 'étape en cours',
    },
    refresh: {
      failed: 'Nous n’avons pas pu mettre à jour l’état. Réessayez.',
      action: 'Mettre à jour l’état',
      lastKnown: 'Dernier état reçu à {{time}}. L’analyse ne s’est pas arrêtée pour autant.',
    },
    partial: {
      text: '{{done}} pages analysées sur {{total}}.',
      pending_one: '1 page manquante : elle n’est pas considérée comme réussie.',
      pending_other: '{{count}} pages manquantes : elles ne sont pas considérées comme réussies.',
      why: 'L’analyse n’a pas pu aller jusqu’au bout : au moins une page n’a pas été analysée (par exemple faute de temps). Chacune figure ci-dessous avec le motif.',
      next: 'Relancez l’analyse pour tenter de couvrir les pages manquantes.',
      nextFull:
        'Ce résultat ne compte pas comme une analyse complète en vigueur. Relancez une analyse complète.',
      nextFullComplete:
        'Ce résultat ne compte pas comme une analyse complète en vigueur. Vous pouvez compléter uniquement les pages en attente : elles sont réanalysées et celles qui ont déjà réussi sont conservées. Seules les pages réanalysées comptent dans votre solde.',
      complete_one: 'Compléter 1 page en attente',
      complete_other: 'Compléter {{count}} pages en attente',
      completing: 'Complément d’analyse…',
      completeFailed:
        'Nous n’avons pas pu compléter l’analyse. Aucune page n’a été utilisée : vous pouvez réessayer.',
    },
    pending: {
      title: 'Pages non analysées',
      hint: 'Elles n’ont pas été analysées et ne comptent pas comme réussies.',
      more: 'Et {{count}} de plus.',
    },
    causes: {
      interrupted: 'L’analyse a été interrompue avant la fin et n’a laissé aucun résultat.',
      domain:
        'Le domaine du site ne correspond pas à une adresse publique. Vérifiez que l’adresse est correcte et que le site est en ligne.',
      limited:
        'Le site a limité les requêtes automatiques (HTTP 429) et aucune page n’a pu être analysée. Ce n’est pas un problème de votre site.',
      blocked:
        'Aucune page n’a pu être analysée : l’analyse automatique n’a pas pu y accéder. Le motif de chacune figure ci-dessous.',
      unreachable: 'Aucune page du site n’a pu être chargée.',
      unknown: 'Nous ne connaissons pas la cause exacte.',
      technical: 'Détail technique',
    },
    consumption: {
      reserved_one:
        'Pendant son exécution, jusqu’à {{count}} page de votre solde est réservée. À la fin, seules les pages analysées sont comptées.',
      reserved_other:
        'Pendant son exécution, jusqu’à {{count}} pages de votre solde sont réservées. À la fin, seules les pages analysées sont comptées.',
      used_one: 'Consommation de cette analyse : {{count}} page.',
      used_other: 'Consommation de cette analyse : {{count}} pages.',
      none: 'Consommation de cette analyse : aucune page.',
      notCounted_one: 'La page qui n’a pas été analysée n’a pas été comptée.',
      notCounted_other: 'Les {{count}} pages qui n’ont pas été analysées n’ont pas été comptées.',
    },
    actions: {
      settings: 'Vérifier la configuration',
      prepare: 'Nouvelle analyse',
      previousResults: 'Voir les résultats de cette analyse',
      seeResults: 'Voir les résultats',
    },
  },
  compare: {
    title: 'Par rapport à l’analyse du {{date}}',
    same: 'Même estimation',
    points_one: '{{signed}} point',
    points_other: '{{signed}} points',
    resolved_one: '{{count}} problème résolu',
    resolved_other: '{{count}} problèmes résolus',
    new_one: '{{count}} nouveau problème',
    new_other: '{{count}} nouveaux problèmes',
  },
  check: {
    title: 'Dernière vérification des corrections',
    titleNote: 'Ce n’est pas une analyse de tout le site',
    scope:
      'Une vérification n’analyse que les pages des éléments vérifiés : ce n’est pas une analyse de tout le site, donc elle ne calcule pas l’estimation et n’est pas comparée aux analyses précédentes.',
    looked: 'Vérifié : {{elements}} sur {{pages}}.',
    elementsCount_one: '{{count}} élément',
    elementsCount_other: '{{count}} éléments',
    pagesCount_one: '{{count}} page',
    pagesCount_other: '{{count}} pages',
    pending: 'Non confirmé automatiquement',
    summaryPending_one:
      '{{count}} élément n’a pas pu être confirmé automatiquement. Son sélecteur n’échoue plus, mais une revue manuelle est nécessaire.',
    summaryPending_other:
      '{{count}} éléments n’ont pas pu être confirmés automatiquement. Leurs sélecteurs n’échouent plus, mais une revue manuelle est nécessaire.',
    summaryPossibleChange_one:
      'L’élément n’échoue plus avec son sélecteur d’origine, mais la page a encore d’autres défauts du même type. L’analyse ne peut pas être sûre que ce soit le même élément : examinez-le à la main.',
    summaryPossibleChange_other:
      '{{count}} éléments n’échouent plus avec leur sélecteur d’origine, mais la page a encore d’autres défauts du même type. L’analyse ne peut pas être sûre que ce soient les mêmes : examinez-les à la main.',
    summaryPersists_one: '{{count}} élément est encore détecté après la vérification.',
    summaryPersists_other: '{{count}} éléments sont encore détectés après la vérification.',
    summaryVerified: 'Les éléments vérifiés ne sont plus détectés dans cette vérification.',
    summaryMixed:
      '{{verified}} non détectés · {{persists}} encore détectés · {{pending}} non confirmés automatiquement.',
    nextPending: 'Revoir dans le suivi',
    nextPersists: 'Voir le suivi',
    nextVerified: 'Voir le suivi',
    details: 'Voir le détail de chaque élément',
    seeResults: 'Voir les pages vérifiées',
    seeFollow: 'Voir le suivi',
  },
  figures: {
    pages: 'Pages analysées',
    failures: 'Problèmes WCAG',
    elements: 'Éléments concernés',
    review: 'Nécessitent une revue manuelle',
  },
  inspector: {
    title: 'Capture d’écran de la page',
    device: 'Appareil de la capture d’écran',
    devices: {
      escritorio: 'Ordinateur',
      movil: 'Mobile',
    },
    frame: 'Capture d’écran de la page (elle défile et peut être agrandie)',
    alt: 'Capture d’écran de {{page}} ({{device}})',
    zoom: { label: 'Agrandir la capture', enlarge: 'Agrandir' },
    reload: 'Recharger la capture',
    loading: 'Chargement de la capture…',
    loadError:
      "La capture n'a pas pu être chargée. Les éléments ne sont pas repérés tant qu'elle ne s'affiche pas.",
    retry: 'Réessayer',
    panHint:
      'Faites glisser la capture d’écran pour vous déplacer, ou placez le focus dessus et utilisez les flèches du clavier.',
    notLocated: {
      'no-capture':
        'Il n’y a pas de capture d’écran pour cet appareil : l’élément ne peut donc pas être marqué.',
      'no-position':
        "Cet élément n'a pas pu être placé sur la capture (il peut être dans un cadre, masqué ou hors de la partie visible). Son sélecteur se trouve sous « Voir la solution technique ».",
      outside:
        'Cet élément se trouve hors de la partie de la page qui a été capturée. Consultez sa description dans le guide.',
      'other-device':
        'Cet élément n’a pas été détecté sur cet appareil. Changez d’appareil pour le voir marqué.',
    },
    noCapture: 'Il n’y a pas de capture d’écran de cette page sur cet appareil.',
    expired:
      'La capture d’écran n’est plus disponible : seules celles des deux dernières analyses de chaque site sont conservées.',
  },
  element: {
    guideTitle: 'Guide pour {{name}}',
    shared: {
      title_one: 'Cet élément est partagé sur {{count}} page',
      title_other: 'Cet élément est partagé sur {{count}} pages',
      here: "Il est géré depuis cette page. Ce que vous indiquez ici s'applique aux {{count}} pages.",
      there: "Il est géré depuis {{page}}. Ce que vous indiquez ici s'applique aussi aux {{count}} pages.",
      open: 'Ouvrir sur {{page}}',
      identical: 'Le code est identique partout.',
      textChanges:
        "Même structure ; le texte ou les adresses changent d'une page à l'autre (par exemple, entre les langues).",
      progress: {
        'sin-aplicar': 'Correction non appliquée sur aucune page.',
        'aplicada-en-parte': 'Correction appliquée sur une partie des pages seulement.',
        aplicada: 'Correction appliquée sur toutes les pages : elle reste à vérifier.',
        'verificada-en-parte':
          "Correction vérifiée sur une partie des pages seulement : le problème n'est pas considéré comme résolu.",
        verificada: 'Correction vérifiée sur toutes les pages.',
      },
      separately:
        'Marquer comme appliquée ne vérifie pas la correction : seule une nouvelle analyse ou une revue manuelle la confirme.',
    },
    panel: {
      back: 'Tous les problèmes',
      problem: 'Problème',
      solution: 'Solution',
      showWhere: 'Voir où c’est',
      backToSolution: 'Retour à la solution',
      captureTitle: 'Où se trouve l’élément',
      element: 'Élément concerné',
      verifyAuto: 'Une fois corrigé, une nouvelle analyse le vérifiera automatiquement.',
      verifyManual:
        'Nécessite une revue manuelle : l’analyse automatique ne peut pas trancher et une personne doit le vérifier.',
      technical: 'Voir la solution technique',
      wcag: 'Références WCAG',
      measured: 'Mesuré',
    },
    nav: {
      label: 'Éléments du problème sur cette page',
      position: 'Élément {{index}} sur {{total}} de cette page',
      previous: 'Précédent',
      next: 'Suivant',
      status: 'Élément {{index}} sur {{total}} de cette page : {{name}}.',
      analysisTotal_one: '{{count}} dans toute l’analyse',
      analysisTotal_other: '{{count}} dans toute l’analyse',
      sample:
        '{{shown}} éléments affichés sur {{total}} : pour les autres, nous ne conservons ni leur emplacement ni leur code.',
      noElements:
        'Ce problème concerne la page entière, et non un élément que l’on puisse désigner. Suivez les étapes ci-dessus.',
    },
    sections: {
      owner: 'Qui peut le faire',
      check: 'Comment le vérifier',
    },
    where: {
      positionValue: 'À {{vertical}} % depuis le haut de la page, {{side}}',
      positionSide: 'Sur la page, {{side}}',
      sides: {
        left: 'à gauche',
        center: 'au centre',
        right: 'à droite',
      },
    },
    roles: {
      desarrollo: 'La personne qui développe ou maintient le code du site',
      diseno: 'La personne qui conçoit ou met en page le site',
      contenido: 'La personne qui modifie les contenus du site (textes, images, liens)',
      unknown: 'La personne qui gère le site',
      note: 'Cela dépend du type de problème. Nous ne savons pas qui gère votre site ni quelles autorisations vous avez : si vous ne pouvez pas le faire vous-même, copiez les instructions pour la personne qui s’en charge.',
    },
    generic: {
      check1: 'Appliquez la modification sur votre site et rechargez la page.',
      check2: 'Vérifiez que cet élément n’a plus le problème décrit.',
      check3:
        'Lancez une nouvelle analyse de la page pour confirmer que SiteNorma ne le détecte plus. Copier ou appliquer une modification ne signifie pas que le problème est corrigé.',
    },
    proposal: {
      adapted: 'Proposition adaptée à cet élément',
      example: 'Exemple à adapter',
      currentCode: 'Code actuel',
      proposedCode: 'Modification proposée ({{language}})',
      copy: 'Copier le code',
      copied: 'Code copié.',
      failed: 'La copie a échoué. Sélectionnez le texte et copiez-le à la main.',
      whereKnown: 'Où l’appliquer : {{where}}.',
      whereUnknown:
        'Nous ne savons pas dans quel fichier ou quel modèle de votre site cette modification doit être appliquée : cherchez-le ou transmettez-le à la personne qui gère le site.',
      notAGuarantee:
        'C’est une proposition, pas une correction assurée : tant qu’une nouvelle analyse ne l’a pas confirmé, le problème reste ouvert.',
    },
    decision: {
      choose: 'Choisissez une option pour voir la proposition.',
    },
    verify: {
      title: 'Vérifier la correction',
      text: 'Copier le code, appliquer une modification ou changer l’état ici ne signifie pas que le problème est corrigé. Pour le confirmer, il faut une vérification : une nouvelle analyse (elle utilise des pages de votre solde et passe toujours par le récapitulatif préalable) ou une revue manuelle.',
      action: 'Vérifier la correction',
    },
    technical: {
      selector: 'Sélecteur',
      location: 'Localisation technique',
      position: 'Position',
      xpath: 'Chemin XPath',
      code: 'Code actuel',
    },
    copy: {
      action: 'Copier les instructions pour le développeur',
      copied: 'Instructions copiées.',
      failed: 'La copie a échoué. Sélectionnez le texte du guide et copiez-le à la main.',
      title: 'SiteNorma · Instructions pour corriger un problème d’accessibilité',
      problem: 'Problème',
      what: 'Ce qui se passe',
      where: 'Où il se trouve',
      element: 'Élément',
      page: 'Page',
      device: 'Appareil',
      position: 'Position approximative',
      date: 'Date de l’analyse',
      evidence: 'Preuves',
      how: 'Comment le corriger',
      currentCode: 'Code actuel',
      check: 'Comment le vérifier',
      selector: 'Sélecteur',
      note: 'Copier ces instructions ou appliquer la modification ne signifie pas que le problème est corrigé : seule une nouvelle analyse de SiteNorma le confirme. L’analyse automatique ne détecte qu’une partie des obstacles d’accessibilité.',
    },
  },
  manual: {
    title: 'Vérifications manuelles',
    intro:
      'Certains points d’accessibilité demandent une revue par une personne. Suivez les instructions et enregistrez ce que vous avez vérifié.',
    available:
      'Vous pouvez commencer une revue quand vous voulez : il n’est pas nécessaire d’attendre d’avoir corrigé les problèmes détectés par l’analyse automatique, et une analyse automatique sans détection ne clôt pas une revue en attente.',
    notAudit:
      'Un guide vous aide à vérifier un point sur une page. Ce n’est pas une évaluation de l’ensemble de votre site, et enregistrer une revue ne signifie pas que votre site respecte une réglementation.',
    readOnly: 'Votre rôle ne permet que de consulter les revues.',
    progress: {
      title: 'Avancement de la revue manuelle',
      summary: '{{registered}} sur {{total}} critères avec au moins une revue enregistrée',
      text: 'Dans la barre, {{registered}} critères sur {{total}} ont au moins une revue enregistrée.',
      started_one: '{{count}} critère de plus a un brouillon non enregistré.',
      started_other: '{{count}} critères de plus ont un brouillon non enregistré.',
      needNew_one: '{{count}} revue est à refaire parce que la page a changé.',
      needNew_other: '{{count}} revues sont à refaire parce que la page a changé.',
      answers: 'Réponses actuelles',
      note: 'Cet avancement ne concerne que les revues manuelles : il est séparé de l’analyse automatique et du suivi du travail. Une revue enregistrée confirme qu’elle a été faite, pas qu’elle a été réussie, et revoir un critère sur une page ne dit rien du reste de votre site.',
    },
    state: {
      'no-iniciada': 'Revue manuelle non commencée',
      pendiente: 'En attente de revue',
      registrada: 'Revue manuelle avec une réponse',
    },
    answer: {
      'sin-problemas': 'Je n’ai trouvé aucun problème',
      problema: 'J’ai trouvé un problème',
      'no-comprobado': 'Je n’ai pas pu le vérifier',
      'no-aplica': 'Sans objet',
    },
    condition: {
      teclado: 'Uniquement au clavier',
      'lector-de-pantalla': 'Avec un lecteur d’écran',
      zoom: 'Avec le zoom ou le texte agrandi',
      'espaciado-de-texto': 'Avec l’espacement du texte modifié',
      movil: 'Sur un mobile ou avec un écran étroit',
      'colores-forzados': 'Avec les couleurs forcées ou un contraste élevé',
      'escala-de-grises': 'En niveaux de gris',
      'sin-estilos': 'Sans styles (sans CSS)',
      'sin-sonido': 'Sans le son',
      'reducir-movimiento': 'Avec « Réduire les animations » activé',
      voz: 'Avec le contrôle vocal',
    },
    answerHint: {
      'sin-problemas':
        'Vous avez suivi le guide et tout ce qui devrait se produire se produit. Ce que vous avez examiné a été vérifié, pas le reste du site.',
      problema:
        'Quelque chose qui devrait se produire ne se produit pas. Décrivez ce que vous avez vu : un problème sera créé.',
      'no-comprobado':
        'Vous n’avez pas pu faire la vérification (il vous manquait un outil, une page ne se chargeait pas…). Ce n’est toujours pas vérifié : ce n’est pas une réponse favorable.',
      'no-aplica':
        'La page n’a pas ce que le guide vérifie (par exemple, il n’y a pas de vidéos). Il faut le justifier.',
    },
    help: {
      guiada: 'Vérification guidée',
      especializada: 'Peut nécessiter l’aide d’un spécialiste',
      none: 'Aucun guide disponible',
    },
    noGuide:
      'Il n’y a pas de guide disponible pour ce point : on ne peut donc pas le vérifier ici en suivant des étapes. Il reste en attente de revue.',
    needsNew: 'Une nouvelle revue est nécessaire',
    needsNewHint:
      'La page a changé depuis la revue. L’évaluation précédente est conservée jusqu’à ce que la revue soit refaite.',
    row: {
      automatic: 'Analyse automatique',
      manual: 'Revue manuelle',
      pages_one: 'sur {{count}} page',
      pages_other: 'sur {{count}} pages',
      reviews_one: '{{count}} revue enregistrée',
      reviews_other: '{{count}} revues enregistrées',
      drafts_one: '{{count}} brouillon',
      drafts_other: '{{count}} brouillons',
      by: '{{name}}, {{date}}',
      someone: 'une personne de votre organisation',
    },
    actions: {
      start: 'Commencer la revue',
      resume: 'Continuer la revue',
      view: 'Voir les revues et refaire la revue',
      repeat: 'Refaire la revue',
      guide: 'Voir le guide',
      back: 'Retour aux résultats',
    },
    page: {
      title: 'Vérification manuelle : {{id}} {{title}}',
      level: 'Niveau {{level}}',
      principle: '{{name}}',
      siteScope:
        'Cette vérification compare plusieurs pages : elle est donc enregistrée pour l’ensemble du site et non pour une seule page.',
      noPages:
        'Aucune page n’a encore été analysée, et cette vérification se fait sur une page. Lancez une analyse, puis revenez.',
      loadFailed: 'Nous n’avons pas pu charger le guide. Réessayez.',
    },
    guide: {
      what: 'Ce que vous allez vérifier',
      how: 'Comment faire',
      expected: 'Ce qui devrait se passer',
      record: 'Comment enregistrer le résultat',
      recordAnswers: 'Choisissez la réponse qui convient :',
      specialist: 'Pourquoi l’aide d’un spécialiste peut être nécessaire',
      version: 'Version du guide : {{version}}.',
    },
    form: {
      title: 'Votre revue',
      identify: 'Ce que vous examinez',
      page: 'Page',
      pageHelp: 'La page que vous avez examinée, parmi celles analysées par SiteNorma.',
      pagePlaceholder: 'Choisissez une page',
      whole: 'Tout le site',
      element: 'Élément',
      elementHelp:
        'Facultatif : la partie de la page que vous avez examinée, par exemple « Menu principal ».',
      device: 'Appareil',
      devices: {
        ambos: 'Ordinateur et mobile',
        escritorio: 'Ordinateur',
        movil: 'Mobile',
      },
      conditions: 'Conditions de la vérification',
      conditionsHelp: 'Cochez ce que vous avez utilisé pour la vérifier.',
      conditionNotes: 'Autres conditions',
      conditionNotesHelp: 'Navigateur et version, lecteur d’écran, taille de la fenêtre…',
      answer: 'Résultat de la vérification',
      answerHelp: 'Si vous ne choisissez aucune réponse, la revue reste en attente.',
      description: 'Ce que vous avez trouvé',
      descriptionHelp:
        'Obligatoire si vous avez trouvé un problème : ce qui ne va pas, où et comment cela se remarque.',
      reason: 'Pourquoi vous n’avez pas pu le vérifier',
      reasonHelp: 'Facultatif, mais cela aide la personne qui reprendra la revue.',
      justification: 'Pourquoi c’est sans objet',
      justificationHelp:
        'Obligatoire : expliquez pourquoi cette page n’a pas ce qui est vérifié (au moins 20 caractères).',
      severity: 'Dans quelle mesure cela gêne l’utilisation du site',
      severities: {
        grave: 'Empêche de terminer une tâche',
        moderada: 'Gêne, mais on peut terminer la tâche',
        leve: 'Dérange, mais n’empêche pas de la faire',
      },
      severityHelp: 'Cela donne sa priorité au problème, comme pour les problèmes de l’analyse automatique.',
      evidence: 'Preuves',
      evidenceHelp:
        'Ce que vous avez vu, les outils et versions utilisés, un lien vers une capture d’écran ou un enregistrement.',
      save: 'Sauvegarder le brouillon',
      saving: 'Sauvegarde…',
      register: 'Enregistrer la revue',
      registering: 'Enregistrement…',
      discard: 'Supprimer le brouillon',
      discardConfirm: 'Supprimer le brouillon',
      discardBody:
        'Ce que vous avez écrit dans ce brouillon est effacé. Les revues enregistrées ne sont pas touchées.',
      cancel: 'Annuler',
    },
    saved: {
      draft: 'Brouillon sauvegardé. La revue reste en attente : il n’y a pas encore de réponse enregistrée.',
      registered:
        'Revue manuelle enregistrée. Cela confirme qu’elle a été sauvegardée ; cela ne signifie pas que la vérification a été réussie.',
      discarded: 'Brouillon supprimé.',
    },
    errors: {
      respuesta: 'Choisissez une réponse pour enregistrer la revue, ou sauvegardez un brouillon.',
      pagina: 'Choisissez la page que vous avez examinée.',
      descripcion: 'Décrivez le problème que vous avez trouvé (au moins 10 caractères).',
      gravedad: 'Indiquez dans quelle mesure cela gêne l’utilisation du site.',
      justificacion: 'Expliquez pourquoi c’est sans objet (au moins 20 caractères).',
      registrada:
        'Cette revue est déjà enregistrée et ne peut pas être modifiée. Refaites-la pour en ajouter une nouvelle.',
      permiso: 'Votre rôle ne permet pas d’enregistrer des revues.',
      'sin-guia': 'Il n’y a pas de guide pour ce point.',
      datos: 'Vérifiez les données du formulaire.',
      generic: 'Nous n’avons pas pu sauvegarder. Réessayez.',
    },
    drafts: {
      title: 'Vos brouillons en cours',
      intro: 'Reprenez-les là où vous vous étiez arrêté.',
      open: 'Continuer ce brouillon',
      updated: 'Sauvegardé le {{date}}',
      start: 'Commencer une nouvelle revue',
    },
    history: {
      title: 'Revues précédentes',
      intro:
        'Elles sont toutes conservées, avec les instructions suivies et leurs preuves : une nouvelle revue s’ajoute aux précédentes et ne les remplace pas.',
      empty: 'Aucune revue de ce point n’a encore été enregistrée.',
      current: 'Évaluation actuelle',
      earlier: 'Précédente',
      by: 'Enregistrée par {{name}}, {{date}}',
      page: 'Page',
      element: 'Élément',
      device: 'Appareil',
      conditions: 'Conditions',
      none: 'Non précisé',
      description: 'Ce qui a été trouvé',
      reason: 'Pourquoi cela n’a pas pu être vérifié',
      justification: 'Pourquoi c’est sans objet',
      evidence: 'Preuves',
      severity: 'Degré de gêne',
      instructions: 'Instructions suivies (version {{version}})',
      afterFix: 'Faite pour vérifier une correction',
      repeats: 'Refait une revue précédente',
    },
    found: {
      origin: 'Détecté lors d’une revue manuelle',
      from: 'Revue manuelle du critère {{id}}',
      by: 'Examiné par {{name}}, {{date}}',
      element: 'Élément : {{element}}',
      device: 'Appareil : {{device}}',
      conditions: 'Conditions : {{list}}',
      evidence: 'Preuves : {{text}}',
      open: 'Voir le guide et refaire la revue',
      note: 'Ce problème vient d’une revue manuelle : il est clos par une revue ultérieure qui ne le trouve plus, et non par une analyse automatique.',
    },
    follow: {
      title: 'Vérification manuelle guidée',
      hint: 'Quand l’analyse automatique ne peut pas trancher, une personne peut refaire la vérification en suivant le guide. Une revue favorable qui couvre tous les appareils où le problème a été détecté permet d’archiver le travail.',
      repeat: 'Refaire le guide « {{title}} »',
      repeatId: 'Refaire le guide du critère {{id}}',
    },
    prepare: {
      title: 'Vérifications qui demandent une personne',
      intro:
        'Pour ces éléments, l’analyse automatique ne peut pas trancher seule. Vous pouvez refaire la vérification avec le guide ; l’analyse ne la clôt pas.',
      repeat: 'Refaire le guide {{id}} pour {{element}}',
    },
  },
  follow: {
    panel: {
      intro:
        "Le statut indique l'avancement de la correction. Pour confirmer que le problème est résolu, il faut le vérifier.",
      kinds: {
        'sin-comprobar': 'Non vérifié',
        pendiente: 'Vérification en attente',
        verificado: 'Vérifié',
        persiste: 'Le problème est toujours détecté',
        descartada: 'Détection écartée',
      },
      sources: { automatica: 'Analyse automatique', manual: 'Vérification manuelle' },
      on: '{{source}} · {{date}}',
      hints: {
        pendiente: 'Analysez à nouveau la page pour le vérifier.',
        persiste: 'Révisez la correction et analysez à nouveau la page.',
        descartada: 'Cette détection ne compte pas comme un problème.',
      },
      manual: "L'analyse automatique ne peut pas trancher ce cas. Une vérification manuelle est nécessaire.",
      recheck: 'Analyser à nouveau',
      check: 'Vérifier la correction',
      advanced: 'Options avancées',
      advancedHint:
        'Validation, mise à l’écart, vérification manuelle, historique et changements sur plusieurs éléments',
      bulk: {
        title: 'Appliquer le statut aux {{count}} éléments',
        hint: 'Sélectionnez le statut à appliquer.',
        choose: 'Choisir un statut',
        apply: 'Appliquer',
        confirmTitle: 'Appliquer le statut à {{count}} éléments ?',
        confirmBody:
          'Le statut du travail de {{count}} éléments va changer. Les résultats de vérification ne sont pas modifiés et chaque élément garde son historique.',
        confirm: 'Appliquer le statut',
        cancel: 'Annuler',
        done: 'Statut appliqué à {{count}} éléments.',
        label: 'Statut à appliquer',
      },
    },
    quick: {
      work: 'État du travail',
      verification: 'Vérification',
      more: 'Plus d’options de suivi',
    },
    title: 'État du problème',
    save: 'Enregistrer l’état',
    saving: 'Enregistrement…',
    notice:
      'Changer l’état dans SiteNorma ne modifie pas votre site. Appliquez la correction avant de la vérifier.',
    readOnly: 'Votre rôle ne permet que de consulter l’état.',
    saved: 'Statut enregistré.',
    savedMany_one:
      'État enregistré sur {{count}} élément, qui conserve son propre historique. Aucune analyse n’a été lancée et aucune page n’a été utilisée.',
    savedMany_other:
      'État enregistré sur {{count}} éléments, chacun avec son propre historique. Aucune analyse n’a été lancée et aucune page n’a été utilisée.',
    errors: {
      justificacion: 'Pour écarter une détection, expliquez pourquoi : au moins 20 caractères.',
      evidencia: 'Pour écarter une détection, indiquez la preuve qui l’appuie : au moins 10 caractères.',
      permiso: 'Votre rôle ne permet pas de modifier l’état.',
      'permiso-descartar':
        'Seuls le propriétaire et les personnes qui administrent l’organisation peuvent écarter une détection.',
      vacio: 'Choisissez ce que vous voulez modifier avant d’enregistrer.',
      demasiados: 'Cela fait trop d’éléments à la fois. Procédez problème par problème.',
      generic: 'Nous n’avons pas pu enregistrer l’état. Réessayez.',
    },
    by: '{{name}}, {{date}}',
    someone: 'une personne de votre organisation',
    system: 'une analyse',
    noteLabel: 'Note',
    noteHint: 'Facultatif. Elle reste dans l’historique avec votre nom et la date.',
    work: {
      title: 'Suivi du travail',
      description: 'Ce que fait votre équipe. Cela ne dit pas si le problème est résolu.',
      statuses: {
        pendiente: 'En attente de correction',
        'en-curso': 'En cours',
        aplicada: 'Correction appliquée',
        aplazada: 'Reportée',
        archivada: 'Travail archivé',
      },
      hints: {
        pendiente: 'Personne n’a commencé à le corriger.',
        'en-curso': 'Quelqu’un de l’équipe y travaille.',
        aplicada:
          'Vous avez modifié le site. Il reste à le vérifier : seule une vérification peut le confirmer.',
        aplazada: 'Il sera corrigé plus tard. Le problème reste dans les résultats et dans l’estimation.',
      },
      reopens: 'Choisir un état rouvre le travail archivé.',
    },
    validation: {
      title: 'Validation de la détection',
      description: 'Indique si la détection est un vrai problème. Cela ne dit pas s’il est corrigé.',
      none: 'Détection automatique, sans validation manuelle',
      statuses: {
        pendiente: 'En attente de validation',
        confirmada: 'Détection confirmée',
        descartada: 'Détection écartée avec justification',
      },
      hints: {
        pendiente: 'Une revue manuelle est nécessaire avant de décider s’il s’agit d’un problème.',
        confirmada:
          'Une personne a vérifié que c’est un vrai problème. Cela ne signifie pas qu’il est corrigé.',
        descartada:
          'Ce n’est pas un vrai problème sur ce site. Il sort des totaux et de l’estimation, et figure à part avec votre justification.',
        revert: 'Retire la décision précédente et valide de nouveau la détection.',
      },
      keep: 'Sans changement',
      adminOnly:
        'Écarter une détection modifie les totaux et l’estimation : seuls le propriétaire et les personnes qui administrent l’organisation peuvent le faire.',
      justification: 'Justification',
      justificationHint: 'Pourquoi ce n’est pas un problème sur ce site. Au moins 20 caractères.',
      evidence: 'Preuve',
      evidenceHint:
        'Ce que vous avez vérifié : un test, une capture d’écran, un lien ou un résultat. Au moins 10 caractères.',
      discardNote:
        'La détection d’origine et son historique sont conservés. L’élément sort des totaux et de l’estimation, et figure à part avec votre justification, votre nom et la date. Vous pouvez rétablir la détection quand vous voulez.',
      decidedBy: 'Décidé par {{name}}, {{date}}.',
      justificationLine: 'Justification : {{text}}',
      evidenceLine: 'Preuve : {{text}}',
    },
    check: {
      title: 'Résultat de la dernière vérification',
      description: 'Ce qu’a trouvé une analyse ultérieure ou une revue manuelle.',
      none: 'Non vérifié',
      noneHint: 'Aucune analyse ni revue n’a vérifié cet élément depuis sa détection.',
      results: {
        'sigue-detectandose': 'Toujours détecté',
        'no-detectado': 'Non détecté lors de la dernière vérification',
        'no-comprobable': 'Non confirmé automatiquement',
        'requiere-revision': 'Revue manuelle requise',
      },
      sources: {
        automatica: 'Analyse automatique',
        manual: 'Revue manuelle',
      },
      on: '{{source}} du {{date}}',
      devices: 'Appareils : {{list}}.',
      stillPending:
        'La correction reste en attente de vérification : un résultat autre que « Non détecté » ne la fait pas passer pour vérifiée.',
      reasons: {
        detectado: 'L’analyse a de nouveau trouvé cet élément.',
        'detectado-en-revision':
          'L’analyse l’a trouvé, mais elle ne peut pas décider s’il s’agit d’un défaut : une revue manuelle est nécessaire.',
        comprobado:
          'Une analyse comparable a examiné la page et ne l’a plus détecté. Cela ne prouve pas que le site respecte la réglementation.',
        'revision-favorable': 'Une personne l’a examiné à la main et il est respecté.',
        'revision-desfavorable': 'Une personne l’a examiné à la main et il n’est toujours pas respecté.',
        'pagina-fuera': 'La page ne figurait pas dans cette analyse : elle n’a donc pas pu être confirmée.',
        'pagina-no-analizada':
          'Le site n’a pas permis d’analyser la page (bloquée, limitée ou en erreur) : on ne sait pas si elle a été corrigée.',
        dispositivo:
          'L’analyse n’a pas couvert tous les appareils où il avait été détecté (ordinateur et mobile) : elle n’est pas comparable.',
        versiones:
          'Cette analyse a utilisé une autre version du moteur ou des règles ; elle n’est donc pas comparable à celle qui a détecté le problème. Vérifiez-le de nouveau.',
        'regla-no-aplicada':
          'Le test n’a trouvé aucun élément de ce type sur la page : vous l’avez peut-être supprimé ou déplacé. Vérifiez-le à la main.',
        muestra:
          'Le test ne liste qu’une partie des éléments de la page et celui-ci n’en fait pas partie : on ne peut pas affirmer qu’il est corrigé.',
        'posible-cambio':
          'Cet élément n’échoue plus avec son sélecteur d’origine, mais la page a encore d’autres défauts du même type. Ce pourrait être le même sous une autre adresse dans le code : l’analyse ne le confirme pas seule. Examinez-le à la main.',
        'ventana-emergente':
          'Il se trouve dans une fenêtre contextuelle ou un avis qui peut ne pas apparaître pendant l’analyse. Examinez-le à la main.',
        'sin-registro':
          'Ce test n’enregistre pas les éléments qui sont respectés : une revue manuelle est nécessaire.',
        incierta:
          'C’est un point que les tests automatiques ne peuvent pas trancher : il faut le vérifier à la main.',
      },
    },
    verify: {
      action: 'Vérifier la correction',
      hint: 'Ouvre le récapitulatif préalable de l’analyse : vous y voyez quelles pages seront examinées et combien de pages elle utilisera. Rien ne démarre tant que vous n’appuyez pas sur le bouton d’analyse.',
      many_one: 'Vérifier la correction appliquée',
      many_other: 'Vérifier les {{count}} corrections appliquées',
    },
    review: {
      title: 'Enregistrer une revue manuelle',
      hint: 'Utilisez-la quand l’analyse ne peut pas trancher (une fenêtre contextuelle, un élément qui a changé de place, un point à examiner) ou quand vous préférez vérifier vous-même. Suivez les étapes de « Comment le vérifier ».',
      outcome: 'Résultat de la revue',
      favorable: 'C’est respecté',
      favorableHint: 'Ce n’est plus un problème sur les appareils que vous avez examinés.',
      desfavorable: 'Toujours non respecté',
      desfavorableHint: 'Le problème est toujours là.',
      devices: 'Appareils que vous avez examinés',
      devicesHint: 'Une revue favorable doit couvrir tous les appareils où il a été détecté.',
      note: 'Ce que vous avez fait et ce que vous avez constaté',
      noteHint: 'Au moins 10 caractères. Elle reste dans l’historique.',
      save: 'Enregistrer la revue',
      saved: 'Revue enregistrée.',
      errors: {
        ambito: 'Une revue favorable doit couvrir tous les appareils où il a été détecté.',
        archivada: 'Ce travail est archivé : rouvrez-le avant d’enregistrer une autre revue.',
        permiso: 'Votre rôle ne permet pas d’enregistrer des revues.',
        generic: 'Nous n’avons pas pu enregistrer la revue. Réessayez.',
      },
    },
    archive: {
      title: 'Archiver le travail de correction',
      evidence: 'Preuve qui le justifie',
      evidenceAuto: 'Une analyse comparable du {{date}} a examiné la page et n’a plus détecté cet élément.',
      evidenceManual: 'Une revue manuelle favorable du {{date}}.',
      noteLabel: 'Note d’archivage',
      noteHint: 'Facultatif.',
      action: 'Archiver le travail de correction',
      archiving: 'Archivage…',
      done: 'Travail archivé.',
      notCertification:
        'Archiver ne prouve pas que le site respecte la réglementation : cela clôt le travail sur cet élément avec la preuve d’une vérification. Si une vérification comparable le détecte de nouveau, le travail est rouvert comme étant en attente.',
      archivedBy: 'Travail archivé le {{date}} par {{name}}.',
      why: {
        'sin-comprobacion':
          'Il pourra être archivé quand une analyse comparable ne détectera plus le problème, ou quand une revue manuelle favorable sera enregistrée.',
        'no-favorable':
          'Il ne peut pas encore être archivé : la dernière vérification n’est pas favorable. Il pourra l’être après une vérification qui ne le détecte pas ou une revue manuelle favorable.',
        'anterior-a-la-correccion':
          'La dernière vérification favorable est antérieure à la correction que vous avez marquée comme appliquée. Vérifiez-la de nouveau.',
        'ya-archivada': 'Le travail est déjà archivé.',
      },
      errors: {
        'sin-evidencia': 'Il n’y a plus de vérification qui justifie l’archivage. Consultez le résultat.',
        permiso: 'Votre rôle ne permet pas d’archiver.',
        generic: 'Nous n’avons pas pu archiver. Réessayez.',
      },
    },
    history: {
      title: 'Historique',
      loading: 'Chargement de l’historique…',
      empty: 'L’historique est encore vide.',
      error: 'Nous n’avons pas pu charger l’historique.',
      note: 'Note : {{text}}',
      created: 'Le suivi de cet élément a commencé.',
      createdMigrated:
        'Une décision antérieure sur le groupe auquel appartenait cet élément est conservée ({{from}}).',
      work: 'Suivi du travail : de « {{from}} » à « {{to}} ».',
      validation: 'Validation de la détection : de « {{from}} » à « {{to}} ».',
      lifted:
        'Une détection écartée a été rétablie ; sa justification était : « {{justification}} » (preuve : « {{evidence}} »).',
      check: 'Vérification automatique : {{result}}. {{reason}}',
      review: 'Revue manuelle ({{outcome}}) sur {{devices}} : {{text}}',
      guided: 'Revue guidée du critère {{criterion}} : {{outcome}}. Appareils : {{devices}}. {{text}}',
      archive: 'Travail de correction archivé.',
      reopenManual: 'Travail archivé rouvert par le choix d’un état.',
      reopenAuto: 'Travail rouvert comme étant en attente : une vérification l’a détecté de nouveau.',
      none: 'aucune décision',
      actor: '{{name}}, {{date}}',
    },
    summary: {
      figures: {
        tracked: 'Éléments suivis',
        applied: 'Correction appliquée',
        verified: 'Vérifiés par contrôle',
      },
      title: 'Suivi du travail et décisions',
      intro:
        'Trois chiffres distincts : combien d’éléments votre équipe suit, pour combien la correction est déclarée, et pour combien un contrôle ne les détecte plus. « Correction appliquée » ne les vérifie pas : il faut les contrôler.',
      none: 'Votre équipe n’a encore rien enregistré sur les éléments de ce site.',
      tracked_one: '{{count}} élément suivi',
      tracked_other: '{{count}} éléments suivis',
      discarded_one:
        '{{count}} détection écartée avec justification : elle ne compte ni dans les totaux ni dans l’estimation.',
      discarded_other:
        '{{count}} détections écartées avec justification : elles ne comptent ni dans les totaux ni dans l’estimation.',
      open: 'Ouvrir le suivi',
    },
    tab: {
      name: 'Suivi',
      title: 'Suivi des corrections',
      intro:
        'L’état du travail (par exemple « Correction appliquée »), le résultat du contrôle et les écarts. Déclarer la correction ne la vérifie pas : il faut la contrôler.',
      viewProblems: 'Voir les problèmes',
      loadFailed: 'Nous n’avons pas pu charger le suivi.',
      empty:
        'Aucune correction enregistrée pour le moment. Commencez par les problèmes détectés et enregistrez l’état d’un élément.',
      filters: 'Filtres',
      work: 'Suivi du travail',
      check: 'Résultat de la vérification',
      all: 'Tous',
      shown_one: 'Affichage de {{shown}} sur {{total}} élément.',
      shown_other: 'Affichage de {{shown}} sur {{total}} éléments.',
      none: 'Aucun élément ne correspond aux filtres.',
      element: 'Élément',
      problem: 'Problème',
      page: 'Page',
      states: 'État',
      open: 'Voir l’élément',
      openPage: 'Voir la page',
      noCapture: 'Il ne figure plus dans la dernière analyse.',
      checkAll: 'Vérifier les corrections appliquées',
      checkAllHint_one:
        'La correction marquée comme appliquée sera vérifiée depuis le récapitulatif préalable de l’analyse.',
      checkAllHint_other:
        'Les {{count}} corrections marquées comme appliquées seront vérifiées depuis le récapitulatif préalable de l’analyse.',
      checkTooMany:
        'Des corrections ont été appliquées sur plus de {{max}} pages : elles seront vérifiées par parties. Ouvrez chacune depuis sa fiche.',
      discards: 'Détections écartées avec justification',
      discardsIntro:
        'Elles sortent des totaux et de l’estimation. La détection d’origine et son historique sont conservés.',
      discardedBy: 'Écartée par {{name}}, {{date}}.',
      lift: 'Pour rétablir une détection écartée, ouvrez l’élément.',
      manage: 'Gérer l’état',
      showArchived: 'Afficher le travail archivé',
    },
    problem: {
      title: 'État du problème',
      counts_one: '{{count}} élément suivi sur {{total}}',
      counts_other: '{{count}} éléments suivis sur {{total}}',
      bulkTitle: 'Appliquer le même état à tous les éléments',
      bulkHint:
        'Il est enregistré séparément sur chaque élément, avec son propre historique. À utiliser quand l’état est le même pour tous.',
      bulkApplies_one: 'Il s’appliquera à {{count}} élément de ce problème.',
      bulkApplies_other: 'Il s’appliquera aux {{count}} éléments de ce problème.',
      discardedElements_one: '{{count}} élément écarté avec justification',
      discardedElements_other: '{{count}} éléments écartés avec justification',
      allDiscarded:
        'Tous les éléments de ce problème sont écartés avec justification : il ne compte ni dans les totaux ni dans l’estimation.',
    },
    discardedList: {
      title: 'Problèmes écartés avec justification',
      intro:
        'Tous leurs éléments sont écartés ; ils ne comptent donc ni dans les totaux ni dans l’estimation. Ouvrez-en un pour voir la justification ou rétablir la détection.',
    },
  },
  site: {
    created: 'Site ajouté. L’analyse n’a pas encore démarré.',
    back: 'Vos sites',
    address: 'Adresse',
    profile: 'Profil réglementaire',
    tabs: {
      pages: 'Pages',
      follow: 'Suivi',
    },
    details: 'Détails du site',
    analysisStandard: 'Norme utilisée pour l’analyse',
    harmonised: 'Référence européenne harmonisée actuelle',
    standardsNote:
      'La référence harmonisée est la version que cite aujourd’hui le Journal officiel de l’Union européenne (décision (UE) 2021/1339). La norme utilisée pour l’analyse dépend du profil réglementaire du site.',
    verificationLink: 'aller à la vérification de la propriété',
    verifyAction: 'Vérifier la propriété',
    notFound: 'Ce site n’existe pas ou n’appartient pas à votre organisation.',
    verification: {
      steps: {
        choose: 'Choisir comment',
        publish: 'Publier le code',
        check: 'Vérifier',
        done: 'terminé',
      },
      title: 'Vérification de la propriété',
      intro: 'Vérifier le site montre qu’il est à vous.',
      without: 'Non vérifié : analyse d’une page et analyse rapide (3 pages).',
      with: 'Propriété vérifiée : analyse complète (jusqu’à 50 pages), qui ne dépend pas du robots.txt.',
      verified: 'Propriété vérifiée le {{date}} ({{method}}).',
      readOnly: 'Demandez à une personne ayant le rôle d’éditeur ou supérieur de la vérifier.',
      method: 'Comment voulez-vous la vérifier ?',
      methods: {
        meta: 'Balise meta',
        dns: 'Enregistrement DNS',
        fichero: 'Fichier',
      },
      methodHints: {
        meta: 'La plus rapide, si vous pouvez modifier le code de la page d’accueil.',
        dns: 'Pour qui gère le domaine ; fonctionne même si un pare-feu bloque le site. Peut prendre quelques heures.',
        fichero: 'Si vous pouvez téléverser des fichiers sur le serveur du site.',
      },
      metaSteps:
        'Collez cette balise dans <head> sur {{url}}, publiez la modification et cliquez sur « Vérifier maintenant » :',
      dnsSteps:
        'Dans le DNS de votre domaine, créez un enregistrement TXT nommé {{host}} avec cette valeur, puis cliquez sur « Vérifier maintenant » :',
      dnsNote: 'Les modifications DNS peuvent mettre quelques heures à apparaître.',
      fileSteps:
        'Créez un fichier texte accessible à {{url}} avec ce contenu et cliquez sur « Vérifier maintenant » :',
      tag: 'Balise',
      record: 'Valeur de l’enregistrement TXT',
      fileContent: 'Contenu du fichier',
      copy: 'Copier',
      copied: 'Copié',
      check: 'Vérifier maintenant',
      checking: 'Vérification en cours…',
      checkingHint:
        'Nous vérifions le code sur votre site. Vous pouvez quitter cet écran ; le résultat apparaîtra ici.',
      checkingWait:
        'La vérification continue. Si le site répond lentement, cela peut prendre encore quelques secondes.',
      checkingSlow:
        'Cela fait plus de 15 secondes. Si le site ne répond pas, la vérification échouera et vous pourrez essayer une autre méthode.',
      done: 'C’est fait : nous avons vérifié la propriété du site.',
      errors: {
        'meta-missing':
          'Nous ne trouvons pas la balise sur {{url}}. Vérifiez qu’elle est bien dans <head> et que la page publiée la contient déjà.',
        'file-missing': 'Le fichier existe, mais il ne contient pas le code. Vérifiez son contenu.',
        'dns-missing':
          'Nous ne voyons pas encore l’enregistrement TXT. Les modifications DNS peuvent mettre quelques heures : réessayez plus tard.',
        http: 'Nous n’avons pas pu terminer la vérification, car le site a renvoyé une erreur HTTP {{status}}. Vérifiez que l’adresse est correcte et que la page est disponible. Vous pouvez aussi essayer une autre méthode de vérification, comme l’enregistrement DNS.',
        unreachable:
          'Nous n’avons pas pu terminer la vérification, car nous n’avons pas pu nous connecter au site. Vérifiez que l’adresse est correcte et que le site est disponible. Vous pouvez aussi essayer une autre méthode de vérification, comme l’enregistrement DNS.',
        timeout: 'La vérification ne s’est pas terminée. Réessayez dans un instant.',
        limite:
          'Vous avez fait beaucoup de vérifications en peu de temps. Patientez un moment puis réessayez.',
      },
    },
    settings: {
      title: 'Paramètres',
      scope:
        'Ces paramètres ne concernent que ce site. Vos données personnelles et votre mot de passe se trouvent dans <profile>Mon profil</profile> et <security>Sécurité</security>.',
      profileUnset:
        'Vous n’avez pas encore choisi de profil réglementaire. Choisissez-le ici ou en préparant une analyse. D’ici là, l’analyse utilise WCAG 2.2 niveau AA comme référence technique.',
      name: 'Nom',
      save: 'Enregistrer les modifications',
      saved: 'Modifications enregistrées.',
      profileNote:
        'Le profil indique seulement quelle réglementation sert de référence lors de l’analyse. Le choisir ne certifie pas et ne prouve pas que votre site respecte la loi, et ne décide pas non plus si elle s’applique à vous : cela dépend de votre activité.', // claims-ok
      cardImage: {
        title: 'Image d’aperçu',
        intro:
          'Mettez à jour la capture de la page d’accueil et l’icône qui apparaissent sur la carte de ce site, dans « Vos sites ».\n\nCette action ne lance pas d’analyse et n’utilise pas de pages de votre forfait. L’image peut aussi se mettre à jour lorsque vous lancez une nouvelle analyse de la page d’accueil.',
        action: 'Mettre à jour l’aperçu',
        working: 'Mise à jour…',
        done: 'La nouvelle image est en cours de capture. Elle apparaîtra dans « Vos sites » dans quelques secondes.',
        errors: {
          permiso: 'Votre rôle ne permet pas de mettre à jour l’image de ce site.',
          limite: 'Vous avez demandé trop de fois de mettre à jour l’image. Attendez un moment et réessayez.',
          generic: 'Nous n’avons pas pu mettre à jour l’image. Réessayez.',
        },
      },
      errors: {
        permiso: 'Votre rôle ne permet pas de modifier les paramètres de ce site. Rien n’a été enregistré.',
        gone: 'Ce site n’existe plus. Rien n’a été enregistré.',
        generic: 'Nous n’avons pas pu enregistrer les modifications. Réessayez.',
      },
    },
    remove: {
      title: 'Supprimer le site',
      intro: 'Le site et toutes ses données seront supprimés. Cette action est irréversible.',
      dataTitle: 'Seront supprimés :',
      data: {
        analyses: 'Les analyses et leurs résultats.',
        follow: 'Le suivi des corrections, avec son historique.',
        reviews: 'Les revues manuelles enregistrées.',
        ownership: 'La vérification de propriété et le lien avec WordPress, le cas échéant.',
      },
      typeName: 'Saisissez « {{name}} » pour confirmer',
      button: 'Supprimer le site',
      confirmTitle: 'Supprimer {{name}} ?',
      confirm: 'Supprimer',
      cancel: 'Annuler',
    },
  },
  invitation: {
    title: 'Invitation à une organisation',
    details: '{{inviter}} vous invite à rejoindre {{organization}} avec le rôle de {{role}}.',
    accept: 'Accepter l’invitation',
    acceptedTitle: 'Invitation acceptée',
    accepted: 'Vous faites désormais partie de l’organisation.',
    continue: 'Aller au tableau de bord',
    loginFirst:
      'Pour l’accepter, connectez-vous ou créez un compte avec l’adresse e-mail à laquelle l’invitation a été envoyée.',
    login: 'Se connecter',
    signup: 'Créer un compte',
    invalid:
      'L’invitation n’est pas valide, a expiré ou est destinée à une autre adresse e-mail. Connectez-vous avec le compte auquel elle a été envoyée ou demandez-en une autre.',
  },
};
